import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Settings, ZoomIn, ZoomOut, SkipForward, SkipBack, PlaySquare, Calendar, RefreshCw, Maximize, X, Pause, Play, ChevronDown, ChevronUp, LogOut } from 'lucide-react';
import styles from './DisplayPage.module.css';
import { useStore, layouts } from '../../../store/useStore';
import { PromoCard } from '../../promotions/components/PromoCard';
import { apiTelaToLocalScreen, buscarTelasPorSequencia, extrairSequenciasUnicasDasTelas, extrairOrdemSequenciaDoJson, extrairOrdemSequenciaPorNomeDoJson, extrairDuracaoSequenciaDoJson, extrairDuracaoSequenciaPorNomeDoJson, extrairDescricaoSequenciaPorNomeDoJson, extrairPosicaoDoJson, adicionarPosicaoAoJson, extrairPaginacaoDoJson, adicionarPaginacaoAoJson, extrairZoomDoJson, adicionarZoomAoJson, extrairTempoPaginacaoDoJson, adicionarTempoPaginacaoAoJson, extrairTempoPanfletoProdutoDoJson, adicionarTempoPanfletoProdutoAoJson, extrairNumeroColunasDoJson, adicionarNumeroColunasAoJson, extrairLarguraColunaDoJson, adicionarLarguraColunaAoJson, adicionarMargensExternasAoJson, jsonToLayoutStyle, layoutStyleFromTelaConfiguracoes, mergeLayoutStyleOnScreenUpdate, bannerSyncService, TIPO_TO_LAYOUT } from '../../banners/services/bannerSyncService';
import { bannerService, TelaBanner, TelaBannerOtimizada, TelaBannerComProcessamentoSeletivo, TelaBannerBasica, ProdutoBanner, resolveProdutoBannerCategoriaNome } from '../../banners/services/bannerService';
import { clearPanfletoFotoBlobCache } from '../../banners/utils/panfletoProdutoFotoBlobCache';
import {
  getDefaultPanfletoElementPlacementsForProductShowcase,
  mergePanfletoContent,
  mergePanfletoElementPlacements,
  mergePanfletoStyles,
  preservePanfletoProductNameAlign,
  readPanfletoContentFromConfigObject,
} from '../../banners/utils/panfletoContentUtils';
import { Screen, Promotion, PanfletoElementPlacements, PanfletoFieldStyle, PanfletoStyleFieldKey } from '../../../shared/types';
import { useBannersOtimizado } from '../../banners/hooks/useBanners';
import { totemModeService } from '../../../shared/services/totemModeService';
import { useScreenWebSocket } from '../hooks/useScreenWebSocket';
import { usePriceUpdate } from '../hooks/usePriceUpdate';
import { televisaoService, Televisao } from '../../televisao/services/televisaoService';
import { Monitor } from 'lucide-react';
import { useDeviceDetection } from '../../../shared/hooks/useDeviceDetection';
import { authService } from '../../auth/services/authService';
import { useTotemSessionExpiryRecovery } from '../../../shared/hooks/useTotemSessionExpiryRecovery';
import { useInchSimulation } from '../hooks/useInchSimulation';
import { DisplayChromePortal, InchSimulationBadge, InchSimulationControl, InchSimulationStage } from '../components/InchSimulationControl';

// Memo para reduzir re-renderizações de cartões de produto durante transições
const MemoPromoCard = React.memo(PromoCard);

// Lista de tipos de unidade (mesma lista dos filtros de promoção)
const TIPOS_UNIDADE_SIGLAS = ['UN', 'KG', 'MT', 'M2', 'M3', 'LT', 'ML', 'SC', 'FD'];

// Função para remover siglas de tipo de unidade do final do nome do produto
// Apenas para visualização - não altera os dados originais
const removeUnitTypeFromName = (name: string): string => {
  if (!name || typeof name !== 'string') return name;
  
  const trimmedName = name.trim();
  if (!trimmedName) return name;
  
  // Verificar se o nome termina com qualquer sigla (case-insensitive)
  for (const sigla of TIPOS_UNIDADE_SIGLAS) {
    // Verificar se termina com espaço + sigla (case-insensitive)
    const regex = new RegExp(`\\s+${sigla.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`, 'i');
    if (regex.test(trimmedName)) {
      // Remover a sigla do final
      return trimmedName.replace(regex, '').trim();
    }
  }
  
  return name;
};

// Componente para carregar vídeo com autenticação (cria Blob URL)
const VideoPlayerWithAuth: React.FC<{
  screenId: number;
  videoLoop: boolean;
  onPlay: () => void;
  onPause: () => void;
  onEnded: () => void;
}> = ({ screenId, videoLoop, onPlay, onPause, onEnded }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let blobUrl: string | null = null;
    let isActive = true;

    const loadVideo = async () => {
      try {
        setLoading(true);
        setError(null);

        const videoBlob = await bannerService.downloadVideo(screenId);

        if (!isActive) return;

        if (videoBlob && videoBlob.size > 0) {
          blobUrl = URL.createObjectURL(videoBlob);
          setVideoBlobUrl(blobUrl);
        } else {
          setError('Vídeo não encontrado ou arquivo vazio');
        }
      } catch (err) {
        if (isActive) {
          console.error('Erro ao carregar vídeo:', err);
          setError(err instanceof Error ? err.message : 'Erro ao carregar vídeo');
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadVideo();

    return () => {
      isActive = false;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
      setVideoBlobUrl(prevUrl => {
        if (prevUrl && prevUrl.startsWith('blob:')) {
          URL.revokeObjectURL(prevUrl);
        }
        return null;
      });
    };
  }, [screenId]);

  // Robust Autoplay Logic: Start Muted -> Play -> Try Unmute
  useEffect(() => {
    if (!videoRef.current || !videoBlobUrl || loading || error) return;

    const video = videoRef.current;

    const startPlayback = async () => {
      try {
        // Step 1: Ensure muted is true for maximum autoplay success chance
        video.muted = true;

        // Step 2: Play
        await video.play();

        // Step 3: Once playing, try to unmute (this might fail if no user interaction exists yet)
        try {
          video.muted = false;
        } catch (unmuteErr) {
          console.warn('Could not unmute video automatically (this is normal until user interacts):', unmuteErr);
          // Keep it muted, but at least it is playing!
        }

        onPlay();
      } catch (err) {
        console.error('Autoplay (muted) failed:', err);
        // If even muted autoplay fails, we are really blocked. 
        // But usually muted autoplay allows "programmatic" play.
      }
    };

    if (video.readyState >= 3) {
      startPlayback();
    } else {
      video.addEventListener('canplay', startPlayback, { once: true });
    }

    return () => {
      video.removeEventListener('canplay', startPlayback);
    };
  }, [videoBlobUrl, loading, error, onPlay]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-xl text-gray-500">Carregando vídeo...</p>
      </div>
    );
  }

  if (error || !videoBlobUrl) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-xl text-gray-500">{error || 'Nenhum vídeo encontrado para esta tela'}</p>
      </div>
    );
  }

  return (
    <div className={styles.videoContainer}>
      <video
        ref={videoRef}
        src={videoBlobUrl}
        className="w-full h-full object-contain"
        autoPlay // Fallback HTML attribute
        muted // Fallback HTML attribute (starts muted to ensure play)
        loop={videoLoop}
        playsInline
        controls={false}
        onPlay={onPlay}
        onPause={onPause}
        onEnded={onEnded}
        onError={(e) => {
          const video = e.target as HTMLVideoElement;
          console.error('Video Error:', video.error);
        }}
      />
    </div>
  );
};

// Interface para armazenar as configurações de posição por tela
interface ScreenPositions {
  [screenId: number]: {
    topOffset: number;
    leftOffset: number;
  };
}

const DisplayPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    screens,
    promotions,
    displayConfig,
    updateDisplayConfig,
    showHeader,
    setShowHeader,
    sequences,
    sequenceConfig,
    currentScreenIndex,
    advanceToNextScreen,
    goToPreviousScreen,
    goToSpecificScreen
  } = useStore();

  // Detecção de dispositivo e ajustes de zoom/resolução
  const { deviceInfo } = useDeviceDetection();
  const inchSimulation = useInchSimulation();

  // Otimização: Ler se é modo totem (agora verifica também URL)
  // Remover useMemo para garantir que leia corretamente o parâmetro da URL se ele mudar ou na navegação inicial
  const isTotemMode = totemModeService.isTotemMode();

  // Só totem: sessão de TV expirada → QR de reconexão (não afeta exibição normal do gestor)
  useTotemSessionExpiryRecovery(isTotemMode);

  const [showSettings, setShowSettings] = useState(false);
  const [showSettingsButton, setShowSettingsButton] = useState(true); // Estado para controlar visibilidade do botão de configurações
  const [isPaginationPaused, setIsPaginationPaused] = useState(false); // Estado de pausa da paginação
  // Inicializar como false para evitar acesso ao DOM durante a renderização inicial (performance)
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentPanfletoIndex, setCurrentPanfletoIndex] = useState(0);
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [showSequenceControls, setShowSequenceControls] = useState(false);
  const [isAdjustingPosition, setIsAdjustingPosition] = useState(false);
  const [screenPositions, setScreenPositions] = useState<ScreenPositions>({});
  const [screenFromAPI, setScreenFromAPI] = useState<Screen | null>(null);
  const [loadingStyles, setLoadingStyles] = useState(false);
  const [telaAPIData, setTelaAPIData] = useState<any>(null); // Armazenar dados brutos da API
  const [telasList, setTelasList] = useState<TelaBannerBasica[]>([]); // Lista de telas da API para exibição (apenas básico: id, nome, descricao, tipo)
  const [loadingTelasList, setLoadingTelasList] = useState(false); // Loading da lista de telas
  const [televisoesList, setTelevisoesList] = useState<Televisao[]>([]); // Lista de televisões para exibição
  const [loadingTelevisoes, setLoadingTelevisoes] = useState(false); // Loading da lista de televisões
  const [savingPosition, setSavingPosition] = useState(false); // Loading ao salvar posição
  /** Modo arrastar blocos do panfleto (display / tela única). */
  const [panfletoPositionEditMode, setPanfletoPositionEditMode] = useState(false);
  const [panfletoPlacementDraft, setPanfletoPlacementDraft] = useState<PanfletoElementPlacements | null>(null);
  const [panfletoFieldStyleDraft, setPanfletoFieldStyleDraft] = useState<Partial<Record<PanfletoStyleFieldKey, PanfletoFieldStyle>> | null>(null);
  /** Campos cujo draft deve substituir o estilo inteiro no save (ex.: colar estilo), sem merge com o da API. */
  const [panfletoFieldStyleReplaceKeys, setPanfletoFieldStyleReplaceKeys] = useState<Partial<Record<PanfletoStyleFieldKey, true>>>({});
  const [savingPanfletoPlacements, setSavingPanfletoPlacements] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false); // Notificação de sucesso
  const successNotificationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flashSuccessNotification = useCallback(() => {
    if (successNotificationTimeoutRef.current) {
      clearTimeout(successNotificationTimeoutRef.current);
    }
    setShowSuccessNotification(true);
    successNotificationTimeoutRef.current = setTimeout(() => {
      setShowSuccessNotification(false);
      successNotificationTimeoutRef.current = null;
    }, 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (successNotificationTimeoutRef.current) {
        clearTimeout(successNotificationTimeoutRef.current);
      }
    };
  }, []);
  /** Baseline da contagem de produtos por tela panfleto (transição 0 → ≥1 reposiciona para o split showcase). */
  const panfletoProductCountScreenIdRef = useRef<number | null>(null);
  const panfletoPrevProductCountRef = useRef<number>(0);
  const [timePerPage, setTimePerPage] = useState(15); // Tempo por página em segundos
  /** Segundos por produto na rotação do panfleto (salvo em configuracoes.tempoPanfletoProduto). */
  const [timePerPanfletoProduct, setTimePerPanfletoProduct] = useState(10);
  /** Força novo GET da foto de fundo do panfleto após TelaAtualizada (ver PromoCard). */
  const [panfletoTelaFotoReloadToken, setPanfletoTelaFotoReloadToken] = useState(0);
  const [progress, setProgress] = useState(0); // Progresso da paginação
  const [productsPerPage, setProductsPerPage] = useState(20); // Quantidade de produtos por página
  const [currentPage, setCurrentPage] = useState(0); // Página atual da paginação
  const [isTransitioning, setIsTransitioning] = useState(false); // Estado para controlar transição de produtos
  // Proteção para View Transitions API (evitar reentrância/estado inválido)
  const viewTransitionInProgressRef = useRef<boolean>(false);
  const lastVTTimeRef = useRef<number>(0);
  const runSafeViewTransition = useCallback((updateFn: () => void) => {
    const hasVT = typeof document !== 'undefined' && 'startViewTransition' in document;
    if (!hasVT) {
      updateFn();
      return;
    }
    if (document.visibilityState !== 'visible') {
      updateFn();
      return;
    }
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        updateFn();
        return;
      }
    } catch {}
    const now = performance.now?.() || Date.now();
    if (now - (lastVTTimeRef.current || 0) < 80) {
      updateFn();
      return;
    }
    if (viewTransitionInProgressRef.current) {
      updateFn();
      return;
    }
    requestAnimationFrame(() => {
      try {
        viewTransitionInProgressRef.current = true;
        const vt = (document as any).startViewTransition(() => {
          updateFn();
        });
        Promise.resolve(vt?.finished)
          .catch(() => {})
          .finally(() => {
            viewTransitionInProgressRef.current = false;
            lastVTTimeRef.current = performance.now?.() || Date.now();
          });
      } catch {
        viewTransitionInProgressRef.current = false;
        updateFn();
      }
    });
  }, []);

  const [showPagination, setShowPagination] = useState(false); // Exibir/ocultar controles de paginação
  const [numeroColunas, setNumeroColunas] = useState(2); // Número de colunas (1, 2 ou 3)
  const [larguraColuna, setLarguraColuna] = useState(100); // Largura da coluna em porcentagem (0-100) - apenas para 1 coluna
  const [showColumnConfig, setShowColumnConfig] = useState(false); // Controlar expansão do submenu de configuração de colunas
  const [externalSpacing, setExternalSpacing] = useState({ // Espaçamento externo (estado local, só salva ao aplicar)
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0
  });
  
  // IMPORTANTE: Salvar valores anteriores de marginRight e marginLeft quando desabilitar (3 colunas)
  // para restaurar quando voltar para 1 ou 2 colunas
  const savedMarginsRef = useRef<{ marginRight: number; marginLeft: number } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const autoAdvanceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const panfletoProductTimerRef = useRef<NodeJS.Timeout | null>(null);
  const wasSettingsButtonVisibleBeforeFullscreen = useRef<boolean>(true); // Guardar estado do botão antes de entrar em fullscreen

  const paginationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const productPaginationTimerRef = useRef<NodeJS.Timeout | null>(null); // Timer separado para paginação de produtos
  const controlsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const progressBarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      clearPanfletoFotoBlobCache();
    };
  }, []);

  // Sincronizar estado de fullscreen com o navegador usando useLayoutEffect para evitar flash na renderização
  useEffect(() => {
    // Sincronização inicial
    setIsFullscreen(!!document.fullscreenElement);

    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);

      // Lógica simples: ao entrar em fullscreen, ocultar botão se estiver visível
      // Ao sair, restaurar se estava visível antes
      // IMPORTANTE: No modo totem, manter o botão visível mesmo em fullscreen (para ajustes urgentes)
      if (isNowFullscreen) {
        // Entrando em fullscreen
        // Só ocultar se não estiver em modo totem
        if (showSettingsButton && !isTotemMode) {
          wasSettingsButtonVisibleBeforeFullscreen.current = true;
          setShowSettingsButton(false);
        }
      } else {
        // Saindo de fullscreen
        // Só restaurar se não estiver em modo totem
        if (wasSettingsButtonVisibleBeforeFullscreen.current && !isTotemMode) {
          setShowSettingsButton(true);
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [showSettingsButton, isTotemMode]);

  // Abrir menu de configurações se o parâmetro settings=true estiver na URL
  useEffect(() => {
    const settingsParam = searchParams.get('settings');
    if (settingsParam === 'true') {
      setShowSettings(true);
      // Remover o parâmetro da URL após abrir o menu
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('settings');
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [searchParams, navigate]);

  // Inicializar modo totem APÓS carregamento
  // Inicializar modo totem: Apenas configurar header, SEM trigger automático
  useEffect(() => {
    // Só prosseguir se estiver em modo totem e NÃO estiver carregando
    if (isTotemMode && !loadingStyles && !loadingTelasList) {
      // Garantir header oculto
      setShowHeader(false);

      // Garantir que body e html não tenham scroll no modo totem
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';

      // NOTA: Removemos o trigger automático de fullscreen pois ele causa bloqueios
      // e comportamentos inconsistentes em navegadores de produção.
      // Agora o usuário deve clicar explicitamente no botão de overlay.
    } else {
      // Restaurar scroll quando não estiver em modo totem
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    // Cleanup: restaurar scroll quando componente desmontar
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isTotemMode, loadingStyles, loadingTelasList, setShowHeader]);

  // Ao entrar na visualização, garantir que o topo da página esteja visível.
  // Sem isso, se a rota herdar um scroll anterior, no panfleto (overflow travado)
  // o menu superior pode parecer "sumido" por ficar fora da viewport.
  useEffect(() => {
    if (isTotemMode || loadingStyles || loadingTelasList || !showHeader) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [isTotemMode, loadingStyles, loadingTelasList, showHeader, id]);

  // Usar o ID da URL para encontrar a tela ativa
  const screenIdFromUrl = id ? parseInt(id, 10) : null;

  // Desabilitar autoLoad quando estiver na página de listagem (sem ID na URL)
  // Para evitar chamadas desnecessárias ao endpoint de telas
  // Só carregar quando realmente precisar (quando houver uma tela sendo visualizada)
  const { telas: telasOtimizadas } = useBannersOtimizado(!!screenIdFromUrl); // Buscar telas otimizadas para sequências apenas quando houver ID

  // Buscar tela do store local
  const localScreen = screenIdFromUrl ? screens.find(s => s.id === screenIdFromUrl) : null;

  // Carregar lista de telas da API quando não houver ID na URL
  // OTIMIZAÇÃO: Usar método otimizado que processa apenas o necessário para listagem
  // Quando o usuário clicar em uma tela, carregamos os dados completos
  useEffect(() => {
    const loadTelasList = async () => {
      if (screenIdFromUrl) {
        // Se há ID, não precisa carregar a lista (já está visualizando uma tela)
        return;
      }

      setLoadingTelasList(true);
      try {
        // SEMPRE usar método otimizado
        // Retorna apenas 4 campos: id, nome, descricao, tipo
        const telas = await bannerService.listarTelasOtimizadoParaDisplay();
        setTelasList(telas);
      } catch (error) {
        console.error('Erro ao carregar telas:', error);
        setTelasList([]);
      } finally {
        setLoadingTelasList(false);
      }
    };

    loadTelasList();
  }, [screenIdFromUrl]);

  // Carregar lista de televisões quando não houver ID na URL
  useEffect(() => {
    const loadTelevisoesList = async () => {
      if (screenIdFromUrl) {
        // Se há ID, não precisa carregar a lista
        return;
      }

      setLoadingTelevisoes(true);
      try {
        const televisoes = await televisaoService.listarTelevisoes();
        setTelevisoesList(televisoes);
      } catch (error) {
        console.error('Erro ao carregar televisões:', error);
        setTelevisoesList([]);
      } finally {
        setLoadingTelevisoes(false);
      }
    };

    loadTelevisoesList();
  }, [screenIdFromUrl]);

  // Carregar dados da API quando o screenId mudar (buscar diretamente da API)
  useEffect(() => {
    const loadScreenFromAPI = async () => {
      if (!screenIdFromUrl || screenIdFromUrl <= 0) {
        setScreenFromAPI(null);
        setLoadingStyles(false);
        return;
      }

      // Se mudou de tela, LIMPAR TUDO da tela anterior imediatamente
      const currentScreenFromAPI = screenFromAPI;
      if (currentScreenFromAPI && currentScreenFromAPI.id !== screenIdFromUrl) {
        setScreenFromAPI(null);
      } else if (!currentScreenFromAPI) {
        // Se não há tela carregada, limpar também
        setScreenFromAPI(null);
      }

      // Iniciar loading imediatamente
      setLoadingStyles(true);

      try {
        // CRÍTICO: Buscar SEM CACHE para garantir dados sempre frescos
        const telasAPI = await bannerService.listarTelas(screenIdFromUrl, true); // noCache = true
        const telaAPI = telasAPI.find(t => t.id === screenIdFromUrl);

        if (telaAPI) {
          // Armazenar dados brutos da API para usar os produtoIds
          setTelaAPIData(telaAPI);

          // Converter da API para formato local (isso já faz o parse das configurações)
          const apiScreen = apiTelaToLocalScreen(telaAPI);

          // Buscar a tela local para pegar outros dados (promotions, etc)
          const currentLocalScreen = screens.find(s => s.id === screenIdFromUrl);

          // Combinar dados: usar dados da API (incluindo configurações) + dados locais (promotions, etc)
          const combinedScreen: Screen = {
            id: screenIdFromUrl,
            name: apiScreen.name || currentLocalScreen?.name || 'Tela sem nome',
            description: apiScreen.description || currentLocalScreen?.description,
            layoutId: apiScreen.layoutId || currentLocalScreen?.layoutId || 'simple2col',
            promotions: currentLocalScreen?.promotions || [],
            linkedProducts: currentLocalScreen?.linkedProducts || [],
            isActive: apiScreen.isActive ?? currentLocalScreen?.isActive ?? false,
            createdAt: currentLocalScreen?.createdAt || new Date().toISOString(),
            backgroundColor: apiScreen.backgroundColor || currentLocalScreen?.backgroundColor,
            // IMPORTANTE: backgroundImage só vem das configurações da API (layoutStyle), não usar fallback local
            backgroundImage: undefined, // Não usar backgroundImage antigo, apenas do layoutStyle
            showHeader: apiScreen.showHeader ?? currentLocalScreen?.showHeader,
            duration: apiScreen.duration || currentLocalScreen?.duration,
            // IMPORTANTE: Usar APENAS layoutStyle convertido das configurações da API (sem fallback local)
            layoutStyle: apiScreen.layoutStyle || undefined
          };

          setScreenFromAPI(combinedScreen);
          // Sincroniza o cabeçalho ao entrar na visualização da tela atual.
          // Sem isso, o estado global pode herdar "oculto" de uma visualização anterior.
          if (!isTotemMode) {
            setShowHeader(combinedScreen.showHeader ?? true);
          }

          // IMPORTANTE: Carregar configurações da tela (posição, paginação, zoom, etc)
          if (telaAPI.configuracoes) {
            applyScreenConfigurations(telaAPI, combinedScreen);
          }
        } else {
          // Se não encontrou na API, usar dados locais
          setScreenFromAPI(localScreen || null);
          if (!isTotemMode) {
            setShowHeader(localScreen?.showHeader ?? true);
          }
        }
      } catch (error) {
        // Em caso de erro, usar dados locais como fallback
        setScreenFromAPI(localScreen || null);
        if (!isTotemMode) {
          setShowHeader(localScreen?.showHeader ?? true);
        }
      } finally {
        // Finalizar loading
        setLoadingStyles(false);
      }
    };

    loadScreenFromAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenIdFromUrl, isTotemMode, localScreen, screens, setShowHeader]);

  // Usar tela da API se disponível
  // IMPORTANTE: Se estiver carregando, NÃO usar localScreen para não mostrar tela anterior
  const activeScreen = screenFromAPI;
  const panfletoLayoutStyleComDraft = useMemo(() => {
    if (!activeScreen?.layoutStyle || !panfletoFieldStyleDraft) {
      return activeScreen?.layoutStyle;
    }
    const prevStyles = activeScreen.layoutStyle.panfletoContent?.styles;
    const mergedStyles = mergePanfletoStyles(prevStyles, panfletoFieldStyleDraft) ?? prevStyles;
    const stylesWithReplace: typeof mergedStyles = { ...(mergedStyles || {}) };
    for (const key of Object.keys(panfletoFieldStyleDraft) as PanfletoStyleFieldKey[]) {
      if (!panfletoFieldStyleReplaceKeys[key]) continue;
      const replaced = panfletoFieldStyleDraft[key];
      if (replaced && Object.keys(replaced).length > 0) {
        stylesWithReplace![key] = replaced;
      } else {
        delete stylesWithReplace![key];
      }
    }
    return {
      ...activeScreen.layoutStyle,
      panfletoContent: mergePanfletoContent({
        ...activeScreen.layoutStyle.panfletoContent,
        styles: stylesWithReplace,
      }),
    };
  }, [activeScreen?.layoutStyle, panfletoFieldStyleDraft, panfletoFieldStyleReplaceKeys]);

  // Função para aplicar configurações da tela
  const applyScreenConfigurations = useCallback((telaAPI: TelaBanner, apiScreen: Partial<Screen>) => {
    if (!telaAPI.configuracoes) return;

    // Carregar posição salva do JSON de configurações
    const posicaoSalva = extrairPosicaoDoJson(telaAPI.configuracoes);
    if (posicaoSalva) {
      setScreenPositions(prev => ({
        ...prev,
        [telaAPI.id!]: {
          topOffset: posicaoSalva.posicaoY ?? 0,
          leftOffset: posicaoSalva.posicaoX ?? 0
        }
      }));
    }

    // Carregar paginação de produtos
    const paginacaoSalva = extrairPaginacaoDoJson(telaAPI.configuracoes);
    setProductsPerPage(paginacaoSalva && paginacaoSalva > 0 ? paginacaoSalva : 20);

    // Carregar zoom
    const zoomSalvo = extrairZoomDoJson(telaAPI.configuracoes);
    updateDisplayConfig({ zoomLevel: zoomSalvo !== undefined ? zoomSalvo : 1 });

    // Carregar tempo de paginação
    const tempoPaginacaoSalvo = extrairTempoPaginacaoDoJson(telaAPI.configuracoes);
    setTimePerPage(tempoPaginacaoSalvo !== undefined && tempoPaginacaoSalvo > 0 ? tempoPaginacaoSalvo : 15);

    const tempoPanfletoSalvo = extrairTempoPanfletoProdutoDoJson(telaAPI.configuracoes);
    if (apiScreen.layoutId === 'panfleto') {
      setTimePerPanfletoProduct(
        tempoPanfletoSalvo !== undefined && tempoPanfletoSalvo > 0 ? tempoPanfletoSalvo : 10
      );
    }

    // Carregar número de colunas
    const numeroColunasSalvo = extrairNumeroColunasDoJson(telaAPI.configuracoes);
    setNumeroColunas(numeroColunasSalvo !== undefined && numeroColunasSalvo >= 1 && numeroColunasSalvo <= 3 ? numeroColunasSalvo : 2);

    // Carregar largura da coluna
    // IMPORTANTE: Sempre carregar do JSON, mas só será aplicada visualmente quando for 1 coluna
    // Quando for 2 ou 3 colunas, o valor será ignorado na renderização, mas mantido no estado
    // Isso permite que o usuário não perca a configuração ao alternar entre números de colunas
    const larguraColunaSalva = extrairLarguraColunaDoJson(telaAPI.configuracoes);
    setLarguraColuna(larguraColunaSalva !== undefined && larguraColunaSalva >= 0 && larguraColunaSalva <= 100 ? larguraColunaSalva : 100);

    // Carregar espaçamento externo
    const containerStyle = apiScreen.layoutStyle?.productContainer;
    const numeroColunasCarregado = numeroColunasSalvo !== undefined && numeroColunasSalvo >= 1 && numeroColunasSalvo <= 3 ? numeroColunasSalvo : 2;
    setExternalSpacing({
      marginTop: containerStyle?.marginTop !== undefined ? containerStyle.marginTop : 0,
      marginRight: numeroColunasCarregado === 3 ? 0 : (containerStyle?.marginRight !== undefined ? containerStyle.marginRight : 0),
      marginBottom: containerStyle?.marginBottom !== undefined ? containerStyle.marginBottom : 0,
      marginLeft: numeroColunasCarregado === 3 ? 0 : (containerStyle?.marginLeft !== undefined ? containerStyle.marginLeft : 0)
    });
    savedMarginsRef.current = null;
  }, [updateDisplayConfig]);

  // Callback para quando o SignalR detectar mudanças na tela
  const handleScreenUpdated = useCallback((updatedScreen: Screen, updatedTelaAPIData: TelaBanner) => {
    console.log('[DisplayPage] Recebendo atualização via SignalR:', {
      screenId: updatedScreen.id,
      temLayoutStyle: !!updatedScreen.layoutStyle,
      temConfiguracoes: !!updatedTelaAPIData.configuracoes
    });

    // CRÍTICO: priceBox, panfletoContent (cor de fundo) se a API enviar config parcial
    setScreenFromAPI(prev => {
      const mergedLayoutStyle = mergeLayoutStyleOnScreenUpdate(
        prev?.layoutStyle,
        updatedScreen.layoutStyle
      );
      const mergedScreen: Screen = {
        ...updatedScreen,
        layoutStyle: mergedLayoutStyle ?? updatedScreen.layoutStyle
      };
      return mergedScreen;
    });
    
    // Atualizar dados da tela da API
    setTelaAPIData(updatedTelaAPIData);

    // Aplicar configurações atualizadas (posição, paginação, zoom, etc)
    applyScreenConfigurations(updatedTelaAPIData, updatedScreen);

    if (updatedScreen.layoutId === 'panfleto') {
      setPanfletoTelaFotoReloadToken((t) => t + 1);
    }

    console.log('[DisplayPage] ✅✅✅ TELA ATUALIZADA - layoutStyle aplicado:', updatedScreen.id);
  }, [applyScreenConfigurations]);

  // Configurar SignalR para receber atualizações em tempo real da tela
  useScreenWebSocket({
    screenId: screenIdFromUrl,
    currentScreen: activeScreen,
    currentTelaAPIData: telaAPIData,
    onScreenUpdated: handleScreenUpdated,
    enabled: !!screenIdFromUrl && !!activeScreen && !!telaAPIData && !loadingStyles // Só ativar quando tela estiver carregada
  });

  // Callback para atualizar preços quando receber evento ProdutoAtualizado
  const handlePricesUpdated = useCallback((precosAtualizados: any[]) => {
    if (!telaAPIData || !precosAtualizados || precosAtualizados.length === 0) {
      return;
    }

    // Criar um mapa de preços atualizados por produtoId
    const precosMap = new Map<number, any>();
    precosAtualizados.forEach(produto => {
      const produtoId = produto.produtoId || produto.id;
      if (produtoId) {
        precosMap.set(produtoId, produto);
      }
    });

    // Atualizar preços nos produtos da tela
    if (!telaAPIData.produtoIds || !Array.isArray(telaAPIData.produtoIds)) {
      return;
    }

    const produtoIdsAtualizados = telaAPIData.produtoIds.map((produto: any) => {
      const produtoId = produto.produtoId || produto.id;
      const precoAtualizado = precosMap.get(produtoId);
      
      if (precoAtualizado) {
        // Atualizar preços mantendo outros dados do produto
        return {
          ...produto,
          vlPreco: precoAtualizado.vlPreco,
          vlPrecoNormal: precoAtualizado.vlPrecoNormal,
          dtPrecoAlterado: precoAtualizado.dtPrecoAlterado
        };
      }
      
      return produto;
    });

    // CRÍTICO: Atualizar telaAPIData com preços atualizados
    setTelaAPIData({
      ...telaAPIData,
      produtoIds: produtoIdsAtualizados
    });
  }, [telaAPIData]);

  // Obter IDs dos produtos da tela atual
  const produtoIds = useMemo(() => {
    if (!telaAPIData?.produtoIds || !Array.isArray(telaAPIData.produtoIds)) {
      return [];
    }
    return telaAPIData.produtoIds
      .map((produto: any) => produto.produtoId || produto.id)
      .filter((id: any) => id != null);
  }, [telaAPIData, screenIdFromUrl]);

  // Configurar SignalR para receber atualizações de preços em tempo real
  usePriceUpdate({
    screenId: screenIdFromUrl,
    produtoIds: produtoIds,
    onPricesUpdated: handlePricesUpdated,
    enabled: !!screenIdFromUrl && !!telaAPIData && produtoIds.length > 0 && !loadingStyles
  });

  // Funções wrapper para navegação de sequências usando URLs
  const navigateToNextScreen = useCallback(() => {
    if (!sequenceConfig.activeSequenceId) return;

    const activeSequence = sequences.find(s => s.id === sequenceConfig.activeSequenceId);
    if (!activeSequence || activeSequence.screens.length === 0) return;

    const orderedScreens = [...activeSequence.screens].sort((a, b) => a.order - b.order);
    const nextIndex = (currentScreenIndex + 1) % orderedScreens.length;
    const nextScreenId = orderedScreens[nextIndex].screenId;

    runSafeViewTransition(() => {
      advanceToNextScreen();
      navigate(`/display/${nextScreenId}`, { replace: true });
    });
  }, [sequenceConfig.activeSequenceId, sequences, currentScreenIndex, advanceToNextScreen, navigate, runSafeViewTransition]);

  const navigateToPreviousScreen = useCallback(() => {
    if (!sequenceConfig.activeSequenceId) return;

    const activeSequence = sequences.find(s => s.id === sequenceConfig.activeSequenceId);
    if (!activeSequence || activeSequence.screens.length === 0) return;

    const orderedScreens = [...activeSequence.screens].sort((a, b) => a.order - b.order);
    const previousIndex = currentScreenIndex === 0
      ? orderedScreens.length - 1
      : currentScreenIndex - 1;
    const previousScreenId = orderedScreens[previousIndex].screenId;

    runSafeViewTransition(() => {
      goToPreviousScreen();
      navigate(`/display/${previousScreenId}`, { replace: true });
    });
  }, [sequenceConfig.activeSequenceId, sequences, currentScreenIndex, goToPreviousScreen, navigate, runSafeViewTransition]);

  const navigateToSpecificScreen = useCallback((index: number) => {
    if (!sequenceConfig.activeSequenceId) return;

    const activeSequence = sequences.find(s => s.id === sequenceConfig.activeSequenceId);
    if (!activeSequence || activeSequence.screens.length === 0 || index < 0 || index >= activeSequence.screens.length) return;

    const orderedScreens = [...activeSequence.screens].sort((a, b) => a.order - b.order);
    const screenId = orderedScreens[index].screenId;

    runSafeViewTransition(() => {
      goToSpecificScreen(index);
      navigate(`/display/${screenId}`, { replace: true });
    });
  }, [sequenceConfig.activeSequenceId, sequences, goToSpecificScreen, navigate, runSafeViewTransition]);

  // Carregar produtos vinculados da API (fonte única de verdade)
  // e filtrar apenas os produtos vinculados à tela ativa
  const activePromotions = useMemo((): Promotion[] => {
    if (!activeScreen) return [];

    const screenId = activeScreen.id;

    // 1. PRIMEIRO: Usar produtoIds diretamente da API (fonte única de verdade)
    if (telaAPIData?.produtoIds && Array.isArray(telaAPIData.produtoIds) && telaAPIData.produtoIds.length > 0) {
      const produtosFromAPI: Promotion[] = telaAPIData.produtoIds.map((produto: any) => {
        // Converter produto da API para formato Promotion
        const nomeOriginal = produto.nome || 'Produto sem nome';
        // IMPORTANTE: Remover sigla de tipo de unidade do final do nome apenas para visualização
        const nomeLimpo = removeUnitTypeFromName(nomeOriginal);
        const categoriaDaApi = resolveProdutoBannerCategoriaNome(produto as ProdutoBanner & Record<string, unknown>);

        const promotion: Promotion = {
          id: produto.produtoId || 0,
          name: nomeLimpo,
          price: produto.vlPreco || 0,
          originalPrice: produto.vlPrecoNormal || undefined,
          unit: produto.tipoUnidade || 'UN',
          image: undefined, // Será carregado depois se necessário
          video: undefined,
          description: undefined,
          category: categoriaDaApi || 'Geral',
          validUntil: produto.dtFinalCampanha
            ? new Date(produto.dtFinalCampanha).toISOString()
            : '',
          validFrom:
            produto.dtInicialCampanha || (produto as { dtInicioCampanha?: string }).dtInicioCampanha
              ? new Date(
                  produto.dtInicialCampanha || (produto as { dtInicioCampanha?: string }).dtInicioCampanha
                ).toISOString()
              : undefined,
          isVideoPromotion: false,
          hasAudio: false,
          autoplay: false,
          loop: false,
          createdAt: new Date().toISOString()
        };

        // Tentar buscar dados completos do store local se disponível
        const localPromo = promotions.find(p => p.id === produto.produtoId);
        if (localPromo) {
          // IMPORTANTE: Remover sigla de tipo de unidade do nome do store local também
          const nomeLocalLimpo = removeUnitTypeFromName(localPromo.name || nomeOriginal);
          const storeCat = (localPromo.category || '').trim();
          const categoryFromStore =
            storeCat && storeCat !== 'Sem categoria' ? storeCat : '';

          return {
            ...promotion,
            name: nomeLocalLimpo,
            image: localPromo.image,
            video: localPromo.video,
            description: localPromo.description,
            category: categoriaDaApi || categoryFromStore || 'Geral',
            isVideoPromotion: localPromo.isVideoPromotion,
            hasAudio: localPromo.hasAudio,
            autoplay: localPromo.autoplay,
            loop: localPromo.loop
          };
        }

        return promotion;
      });

      return produtosFromAPI;
    }

    // 2. Fallback: usar snapshots salvos no store (linkedProducts) - mais completo
    if (activeScreen.linkedProducts && activeScreen.linkedProducts.length > 0) {
      return activeScreen.linkedProducts.map(linkedProduct => {
        // IMPORTANTE: Remover sigla de tipo de unidade do nome apenas para visualização
        const nomeLimpo = removeUnitTypeFromName(linkedProduct.name);
        
        return {
          id: linkedProduct.id,
          name: nomeLimpo,
          price: linkedProduct.price,
          originalPrice: linkedProduct.originalPrice,
          unit: linkedProduct.unit,
          image: linkedProduct.image,
          video: linkedProduct.video,
          description: linkedProduct.description,
          category: linkedProduct.category,
          validUntil: linkedProduct.validUntil,
          isVideoPromotion: linkedProduct.isVideoPromotion,
          hasAudio: linkedProduct.hasAudio,
          autoplay: linkedProduct.autoplay,
          loop: linkedProduct.loop,
          createdAt: linkedProduct.linkedAt
        };
      });
    }

    // 3. Último fallback: filtrar promotions do store usando IDs (vindos da API via screenFromAPI)
    if (activeScreen.promotions && activeScreen.promotions.length > 0) {
      const filtered = promotions.filter((p: Promotion) => activeScreen.promotions.includes(p.id));
      // IMPORTANTE: Remover sigla de tipo de unidade do nome apenas para visualização
      return filtered.map(promo => ({
        ...promo,
        name: removeUnitTypeFromName(promo.name)
      }));
    }

    return [];
  }, [activeScreen, promotions, telaAPIData?.produtoIds ? JSON.stringify(telaAPIData.produtoIds.map((p: any) => ({ id: p.produtoId || p.id, vlPreco: p.vlPreco, vlPrecoNormal: p.vlPrecoNormal }))) : null]); // Dependência específica incluindo preços para forçar recálculo

  // Obter a posição atual da tela ativa ou usar valor padrão
  const getCurrentTopOffset = useCallback(() => {
    if (!activeScreen) return 0;
    return screenPositions[activeScreen.id]?.topOffset || 0;
  }, [activeScreen, screenPositions]);

  const getCurrentLeftOffset = useCallback(() => {
    if (!activeScreen) return 0;
    return screenPositions[activeScreen.id]?.leftOffset || 0;
  }, [activeScreen, screenPositions]);

  // Ajustar a posição vertical
  const adjustVerticalPosition = useCallback((increment: boolean) => {
    if (!activeScreen || !isAdjustingPosition) return;

    const currentOffset = getCurrentTopOffset();
    // Quando for 1 coluna, permitir um range maior de ajuste para chegar às extremidades (superior e inferior)
    const maxOffset = numeroColunas === 1 ? 1000 : 300;
    const newOffset = increment
      ? Math.min(currentOffset + 10, maxOffset) // Limitar deslocamento máximo para baixo
      : Math.max(currentOffset - 10, -maxOffset); // Limitar deslocamento máximo para cima

    setScreenPositions(prev => ({
      ...prev,
      [activeScreen.id]: {
        topOffset: newOffset,
        leftOffset: prev[activeScreen.id]?.leftOffset || 0
      }
    }));
  }, [activeScreen, isAdjustingPosition, getCurrentTopOffset, numeroColunas]);

  // Ajustar a posição horizontal
  const adjustHorizontalPosition = useCallback((increment: boolean) => {
    if (!activeScreen || !isAdjustingPosition) return;

    const currentOffset = getCurrentLeftOffset();
    // Quando for 1 coluna, permitir um range maior de ajuste para chegar às extremidades
    const maxOffset = numeroColunas === 1 ? 1000 : 300;
    const newOffset = increment
      ? Math.min(currentOffset + 10, maxOffset) // Limitar deslocamento máximo para direita
      : Math.max(currentOffset - 10, -maxOffset); // Limitar deslocamento máximo para esquerda

    setScreenPositions(prev => ({
      ...prev,
      [activeScreen.id]: {
        topOffset: prev[activeScreen.id]?.topOffset || 0,
        leftOffset: newOffset
      }
    }));
  }, [activeScreen, isAdjustingPosition, getCurrentLeftOffset, numeroColunas]);

  // Menu de exibição: JSON fresco da API + patch SÓ das chaves de exibição.
  // Não regenera estilo, não manda produtoIds.
  const handleSavePosition = useCallback(async () => {
    if (!activeScreen || !telaAPIData) return;

    setSavingPosition(true);
    try {
      const currentTopOffset = getCurrentTopOffset();
      const currentLeftOffset = getCurrentLeftOffset();

      const telasAPI = await bannerService.listarTelasOtimizado(activeScreen.id);
      const telaAtual = telasAPI.find(t => t.id === activeScreen.id);

      if (!telaAtual) {
        throw new Error('Tela não encontrada na API');
      }

      let configuracoesAtualizadas = adicionarPosicaoAoJson(
        telaAtual.configuracoes,
        currentLeftOffset,
        currentTopOffset
      );

      configuracoesAtualizadas = adicionarPaginacaoAoJson(
        configuracoesAtualizadas,
        productsPerPage
      );

      if (activeScreen.layoutId !== 'panfleto') {
        configuracoesAtualizadas = adicionarZoomAoJson(
          configuracoesAtualizadas,
          displayConfig.zoomLevel
        );
      }

      configuracoesAtualizadas = adicionarTempoPaginacaoAoJson(
        configuracoesAtualizadas,
        timePerPage
      );

      configuracoesAtualizadas = adicionarNumeroColunasAoJson(
        configuracoesAtualizadas,
        numeroColunas
      );

      configuracoesAtualizadas = adicionarLarguraColunaAoJson(
        configuracoesAtualizadas,
        larguraColuna
      );

      // Espaçamento externo = só margins do productContainer (resto do estilo intacto)
      configuracoesAtualizadas = adicionarMargensExternasAoJson(
        configuracoesAtualizadas,
        {
          marginTop: externalSpacing.marginTop,
          marginRight: numeroColunas === 3 ? 0 : externalSpacing.marginRight,
          marginBottom: externalSpacing.marginBottom,
          marginLeft: numeroColunas === 3 ? 0 : externalSpacing.marginLeft,
        }
      );

      if (activeScreen.layoutId === 'panfleto') {
        configuracoesAtualizadas = adicionarTempoPanfletoProdutoAoJson(
          configuracoesAtualizadas,
          Math.max(1, timePerPanfletoProduct)
        );
      }

      if (!configuracoesAtualizadas) {
        throw new Error('Erro ao atualizar configurações');
      }

      const command: any = {
        id: activeScreen.id,
        nome: telaAtual.nome,
        tipo: telaAtual.tipo ?? 0,
        configuracoes: configuracoesAtualizadas
      };

      await bannerService.atualizarTela(command);

      // Recarregar dados da API para garantir sincronização
      const telasRecarregadas = await bannerService.listarTelas(activeScreen.id);
      const telaRecarregada = telasRecarregadas.find(t => t.id === activeScreen.id);

      if (telaRecarregada) {
        setTelaAPIData(telaRecarregada);
        // Atualizar screenFromAPI com os dados atualizados (incluindo layoutStyle com espaçamento externo)
        const apiScreen = apiTelaToLocalScreen(telaRecarregada);
        setScreenFromAPI(prev => {
          if (!prev) return null;
          const mergedLayoutStyle = mergeLayoutStyleOnScreenUpdate(
            prev.layoutStyle,
            apiScreen.layoutStyle
          );
          return {
            ...prev,
            layoutStyle: mergedLayoutStyle ?? apiScreen.layoutStyle ?? prev.layoutStyle
          };
        });
      }

      flashSuccessNotification();
    } catch (error) {
      console.error('Erro ao salvar posição:', error);
      // Manter alert para erros (pode melhorar depois se necessário)
      alert('Erro ao salvar posição: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
    } finally {
      setSavingPosition(false);
    }
  }, [activeScreen, telaAPIData, getCurrentTopOffset, getCurrentLeftOffset, productsPerPage, timePerPage, timePerPanfletoProduct, displayConfig.zoomLevel, numeroColunas, larguraColuna, externalSpacing, flashSuccessNotification]);

  const handlePanfletoFieldStyleDraftChange = useCallback(
    (field: PanfletoStyleFieldKey, patch: Partial<PanfletoFieldStyle>, options?: { replace?: boolean }) => {
      setPanfletoFieldStyleDraft((prev) => {
        if (options?.replace) {
          return {
            ...(prev || {}),
            [field]: patch,
          };
        }
        const prevField = prev?.[field];
        let nextField: PanfletoFieldStyle = { ...(prevField || {}), ...patch };
        if (field === 'linha1') {
          nextField = preservePanfletoProductNameAlign(prevField, nextField) ?? nextField;
        }
        return {
          ...(prev || {}),
          [field]: nextField,
        };
      });
      setPanfletoFieldStyleReplaceKeys((prev) => {
        if (options?.replace) {
          return { ...prev, [field]: true };
        }
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    []
  );

  /**
   * Persiste apenas drafts de posição/estilo do panfleto.
   * Estratégia segura: busca o JSON fresco da API, preserva TODAS as chaves raiz e de
   * `panfletoContent`, e só sobrescreve `elementPlacements` / `styles` quando houver draft.
   * Não reescreve o bloco via `mergePanfletoContent` no PUT (evita injetar defaults e apagar campos).
   */
  const handleSavePanfletoPlacements = useCallback(async () => {
    if (
      !activeScreen ||
      !telaAPIData ||
      activeScreen.layoutId !== 'panfleto' ||
      (!panfletoPlacementDraft && !panfletoFieldStyleDraft)
    ) {
      return;
    }
    setSavingPanfletoPlacements(true);
    try {
      const telasAPI = await bannerService.listarTelasOtimizado(activeScreen.id);
      const telaAtual = telasAPI.find(t => t.id === activeScreen.id);
      if (!telaAtual) {
        throw new Error('Tela não encontrada na API');
      }
      if (!telaAtual.configuracoes || !String(telaAtual.configuracoes).trim()) {
        throw new Error('Configurações da tela vazias na API — abortando para não sobrescrever com JSON incompleto');
      }

      let config: Record<string, unknown>;
      try {
        config = JSON.parse(telaAtual.configuracoes) as Record<string, unknown>;
      } catch {
        throw new Error('JSON de configurações inválido na API — abortando save');
      }
      if (!config || typeof config !== 'object' || Array.isArray(config)) {
        throw new Error('Configurações da tela em formato inesperado — abortando save');
      }

      const rawPc = readPanfletoContentFromConfigObject(config);
      // Cópia rasa do objeto da API (não passar por merge/defaults no write).
      const nextPc: Record<string, unknown> = rawPc && typeof rawPc === 'object' ? { ...(rawPc as object) } : {};

      if (panfletoPlacementDraft) {
        const normalizedPlacements =
          mergePanfletoElementPlacements(
            (rawPc?.elementPlacements as PanfletoElementPlacements | undefined) ?? undefined,
            panfletoPlacementDraft
          ) ?? panfletoPlacementDraft;
        nextPc.elementPlacements = normalizedPlacements;
      }

      if (panfletoFieldStyleDraft) {
        // Só mescla/normaliza os campos alterados no draft; demais estilos ficam iguais aos da API.
        // Campos marcados com replace (colar estilo) substituem o bloco inteiro daquele campo.
        const prevStyles = {
          ...((rawPc?.styles as Record<string, PanfletoFieldStyle> | undefined) || {}),
        };
        const patched = mergePanfletoStyles(prevStyles, panfletoFieldStyleDraft) ?? {};
        const nextStyles: Record<string, PanfletoFieldStyle> = { ...prevStyles };
        for (const key of Object.keys(panfletoFieldStyleDraft) as PanfletoStyleFieldKey[]) {
          if (panfletoFieldStyleReplaceKeys[key]) {
            const replaced = panfletoFieldStyleDraft[key];
            if (replaced && Object.keys(replaced).length > 0) {
              nextStyles[key] = replaced;
            } else {
              delete nextStyles[key];
            }
            continue;
          }
          if (patched[key]) {
            nextStyles[key] = patched[key]!;
          } else {
            delete nextStyles[key];
          }
        }
        const keptLinha1 = preservePanfletoProductNameAlign(prevStyles.linha1, nextStyles.linha1);
        if (keptLinha1) {
          nextStyles.linha1 = keptLinha1;
        }
        nextPc.styles = nextStyles;
      }

      config.panfletoContent = nextPc;

      // Não enviar produtoIds: mesmo padrão de atualizarTelaAPI ao salvar só configurações
      // (omitir = backend não altera vínculos de produtos).
      const payload: any = {
        id: activeScreen.id,
        nome: telaAtual.nome,
        tipo: telaAtual.tipo ?? 0,
        configuracoes: JSON.stringify(config),
      };
      await bannerService.atualizarTela(payload);

      const telasRecarregadas = await bannerService.listarTelas(activeScreen.id);
      const telaRecarregada = telasRecarregadas.find(t => t.id === activeScreen.id);
      if (telaRecarregada) {
        setTelaAPIData(telaRecarregada);
        const apiScreen = apiTelaToLocalScreen(telaRecarregada);
        setScreenFromAPI(prev => {
          if (!prev) return null;
          const mergedLayoutStyle = mergeLayoutStyleOnScreenUpdate(prev.layoutStyle, apiScreen.layoutStyle);
          return {
            ...prev,
            layoutStyle: mergedLayoutStyle ?? apiScreen.layoutStyle ?? prev.layoutStyle,
          };
        });
      }

      setPanfletoPositionEditMode(false);
      setPanfletoPlacementDraft(null);
      setPanfletoFieldStyleDraft(null);
      setPanfletoFieldStyleReplaceKeys({});
      flashSuccessNotification();
    } catch (error) {
      console.error('Erro ao salvar posições do panfleto:', error);
      alert(
        'Erro ao salvar posições do panfleto: ' + (error instanceof Error ? error.message : 'Erro desconhecido')
      );
    } finally {
      setSavingPanfletoPlacements(false);
    }
  }, [activeScreen, telaAPIData, panfletoPlacementDraft, panfletoFieldStyleDraft, panfletoFieldStyleReplaceKeys, flashSuccessNotification]);

  /**
   * ÚNICO ponto de save do botão "Aplicar ajustes".
   * INVARIANTE: a tecla Enter deve sempre chamar esta função (nunca um save paralelo).
   * Enter = atalho do botão; os dois precisam continuar andando juntos.
   */
  const handleApplyAdjustments = useCallback(() => {
    const shouldSavePanfleto =
      activeScreen?.layoutId === 'panfleto' &&
      (panfletoPositionEditMode || !!panfletoPlacementDraft || !!panfletoFieldStyleDraft);

    if (shouldSavePanfleto) {
      void handleSavePanfletoPlacements();
      return;
    }

    void handleSavePosition();
  }, [
    activeScreen?.layoutId,
    panfletoPositionEditMode,
    panfletoPlacementDraft,
    panfletoFieldStyleDraft,
    handleSavePanfletoPlacements,
    handleSavePosition,
  ]);

  useEffect(() => {
    setPanfletoPositionEditMode(false);
    setPanfletoPlacementDraft(null);
    setPanfletoFieldStyleDraft(null);
    setPanfletoFieldStyleReplaceKeys({});
  }, [screenIdFromUrl]);

  // IMPORTANTE: Gerenciar marginRight e marginLeft baseado em numeroColunas
  // Quando for 3 colunas: salvar valores anteriores e forçar 0
  // Quando voltar para 1 ou 2 colunas: restaurar valores anteriores
  useEffect(() => {
    const currentMarginRight = externalSpacing.marginRight;
    const currentMarginLeft = externalSpacing.marginLeft;
    
    if (numeroColunas === 3) {
      // Se mudou para 3 colunas e ainda não salvou os valores anteriores, salvar
      if (savedMarginsRef.current === null) {
        savedMarginsRef.current = {
          marginRight: currentMarginRight,
          marginLeft: currentMarginLeft
        };
      }
      
      // Forçar valores para 0 na visualização
      if (currentMarginRight !== 0 || currentMarginLeft !== 0) {
        setExternalSpacing(prev => ({
          ...prev,
          marginRight: 0,
          marginLeft: 0
        }));
      }
    } else if (numeroColunas === 1 || numeroColunas === 2) {
      // Se voltou para 1 ou 2 colunas e há valores salvos, restaurar
      if (savedMarginsRef.current !== null) {
        const { marginRight, marginLeft } = savedMarginsRef.current;
        // Só restaurar se os valores atuais forem 0 (ou seja, foram zerados quando estava em 3 colunas)
        if (currentMarginRight === 0 && currentMarginLeft === 0) {
          setExternalSpacing(prev => ({
            ...prev,
            marginRight: marginRight,
            marginLeft: marginLeft
          }));
        }
        // Limpar valores salvos após restaurar
        savedMarginsRef.current = null;
      }
    }
  }, [numeroColunas]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mostrar controles brevemente quando a tela muda
  useEffect(() => {
    // Mostrar os controles
    setShowControls(true);

    // Limpar timer existente, se houver
    if (controlsTimerRef.current) {
      clearTimeout(controlsTimerRef.current);
    }

    // Configurar timer para ocultar os controles após 3 segundos
    controlsTimerRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);

    return () => {
      if (controlsTimerRef.current) {
        clearTimeout(controlsTimerRef.current);
      }
    };
  }, [screenIdFromUrl, currentScreenIndex]);

  // Filtrar promoções conforme o layout ativo
  const filteredPromotions = useMemo((): Promotion[] => {
    if (!activeScreen) return [];
    if (activeScreen.layoutId === 'video') {
      return activePromotions.filter((p: Promotion) => p.isVideoPromotion);
    }
    return activePromotions.filter((p: Promotion) => !p.isVideoPromotion);
  }, [activeScreen, activePromotions]);

  // Panfleto: ao inserir o primeiro produto (0 → ≥1), aplicar posições padrão do split texto|foto (borda compartilhada).
  // Ao voltar a 0 produtos não alteramos placements; ao mudar de tela, só sincronizamos o baseline (sem reset).
  useEffect(() => {
    if (activeScreen?.layoutId !== 'panfleto') {
      panfletoProductCountScreenIdRef.current = null;
      return;
    }
    const sid = activeScreen.id;
    const count = filteredPromotions.length;
    if (panfletoProductCountScreenIdRef.current !== sid) {
      panfletoProductCountScreenIdRef.current = sid;
      panfletoPrevProductCountRef.current = count;
      return;
    }
    const prev = panfletoPrevProductCountRef.current;
    if (prev === 0 && count > 0) {
      setPanfletoPlacementDraft(getDefaultPanfletoElementPlacementsForProductShowcase());
    }
    panfletoPrevProductCountRef.current = count;
  }, [activeScreen?.id, activeScreen?.layoutId, filteredPromotions.length]);

  // Valor total de produtos (estabilizado para evitar mudanças durante edição)
  // Usar apenas produtoIds da API como fonte de verdade para o total
  const totalProductsCount = useMemo(() => {
    // Priorizar produtoIds da API (fonte única de verdade)
    if (telaAPIData?.produtoIds && Array.isArray(telaAPIData.produtoIds)) {
      return telaAPIData.produtoIds.length;
    }
    // Fallback para filteredPromotions se não houver produtoIds
    return filteredPromotions.length;
  }, [
    // Usar comprimento e string JSON dos IDs para garantir estabilidade
    telaAPIData?.produtoIds?.length,
    telaAPIData?.produtoIds ? JSON.stringify(telaAPIData.produtoIds.map((p: any) => p?.produtoId || p?.id)) : null,
    filteredPromotions.length
  ]);

  // Função helper para mudar de página com transição usando View Transitions API
  const changePageWithTransition = useCallback((newPage: number) => {
    if (isTransitioning) return;
    // Transição leve baseada em opacidade (sem snapshots de View Transitions)
    setCurrentPage(newPage);
  }, [isTransitioning, runSafeViewTransition]);

  // Calcular paginação de produtos (apenas para simple2col)
  const totalPages = useMemo(() => {
    if (!activeScreen || activeScreen.layoutId !== 'simple2col' || filteredPromotions.length === 0) {
      return 1;
    }
    const safeProductsPerPage = Math.max(1, productsPerPage);
    return Math.ceil(filteredPromotions.length / safeProductsPerPage);
  }, [filteredPromotions.length, productsPerPage, activeScreen]);

  // Produtos da página atual (apenas para simple2col)
  const paginatedPromotions = useMemo((): Promotion[] => {
    if (!activeScreen || activeScreen.layoutId !== 'simple2col') {
      return filteredPromotions;
    }
    const safeProductsPerPage = Math.max(1, productsPerPage);
    const startIndex = currentPage * safeProductsPerPage;
    const endIndex = startIndex + safeProductsPerPage;
    return filteredPromotions.slice(startIndex, endIndex);
  }, [filteredPromotions, currentPage, productsPerPage, activeScreen]);

  // Resetar página quando mudar de tela ou produtos
  // Comentado temporariamente para evitar conflitos com paginação automática
  /* useEffect(() => {
    setCurrentPage(0);
  }, [screenIdFromUrl, filteredPromotions.length]); */

  // Ajustar página atual se estiver fora dos limites
  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(totalPages - 1);
    }
  }, [currentPage, totalPages]);

  // Verificar se a tela atual deve ser exibida com base no dia da semana
  const shouldDisplayCurrentScreen = useCallback(() => {
    // Se não estamos usando programação por dia da semana, sempre exibir
    if (sequenceConfig.scheduleType !== 'time') {
      return true;
    }

    // Se não há sequência ativa, sempre exibir
    const activeSequence = sequences.find(s => s.id === sequenceConfig.activeSequenceId);
    if (!activeSequence) {
      return true;
    }

    // Obter o dia da semana atual (0 = domingo, 1 = segunda, etc.)
    const currentDayOfWeek = new Date().getDay();

    // Encontrar o item de sequência atual
    const currentSequenceItem = activeSequence.screens
      .sort((a, b) => a.order - b.order)
      .find((item, index) => index === currentScreenIndex);

    // Se o item não tem dias definidos ou inclui o dia atual, exibir
    if (!currentSequenceItem?.daysOfWeek || currentSequenceItem.daysOfWeek.includes(currentDayOfWeek)) {
      return true;
    }

    // Caso contrário, não exibir esta tela
    return false;
  }, [sequenceConfig.scheduleType, sequenceConfig.activeSequenceId, sequences, currentScreenIndex]);

  // Avançar para a próxima tela válida para o dia atual
  const advanceToNextValidScreen = useCallback(() => {
    if (!sequenceConfig.activeSequenceId) return;

    const activeSequence = sequences.find(s => s.id === sequenceConfig.activeSequenceId);
    if (!activeSequence || activeSequence.screens.length === 0) return;

    // Ordenar as telas por ordem
    const orderedScreens = [...activeSequence.screens].sort((a, b) => a.order - b.order);

    // Obter o dia da semana atual (0 = domingo, 1 = segunda, etc.)
    const currentDayOfWeek = new Date().getDay();

    // Índice inicial para começar a verificação
    let nextIndex = (currentScreenIndex + 1) % orderedScreens.length;
    const startIndex = nextIndex;

    // Percorrer todas as telas até encontrar uma válida para o dia atual
    do {
      // Verificar se esta tela deve ser exibida hoje
      const screen = orderedScreens[nextIndex];

      // Se a tela não tem dias definidos ou inclui o dia atual, exibir
      if (!screen.daysOfWeek || screen.daysOfWeek.includes(currentDayOfWeek)) {
        navigateToSpecificScreen(nextIndex);
        return;
      }

      // Passar para a próxima tela
      nextIndex = (nextIndex + 1) % orderedScreens.length;
    } while (nextIndex !== startIndex); // Parar se já verificamos todas as telas

    // Se chegamos aqui, nenhuma tela é válida para hoje, então não avançamos
  }, [sequenceConfig.activeSequenceId, sequences, currentScreenIndex, navigateToSpecificScreen]);

  // Função para obter classes CSS do grid baseado no número de colunas
  const getGridClasses = useCallback(() => {
    if (numeroColunas === 1) {
      return "w-full grid grid-cols-1 gap-3 max-w-full px-0";
    } else if (numeroColunas === 3) {
      return "w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-w-full px-0";
    } else {
      // Padrão: 2 colunas
      return "w-full grid grid-cols-1 md:grid-cols-2 gap-3 max-w-full px-0";
    }
  }, [numeroColunas]);

  const handleZoom = (increment: boolean) => {
    const newZoom = increment
      ? Math.min(displayConfig.zoomLevel + 0.1, 2) // Max zoom: 200%
      : Math.max(displayConfig.zoomLevel - 0.1, 0.5); // Min zoom: 50%
    updateDisplayConfig({ zoomLevel: Number(newZoom.toFixed(1)) });
  };

  // Function to handle slideshow transition for panfleto layout
  const nextPanfletoItem = useCallback(() => {
    if (activeScreen?.layoutId === 'panfleto' && filteredPromotions.length > 1) {
      if (currentPanfletoIndex === filteredPromotions.length - 1) {
        if (sequenceConfig.activeSequenceId && sequenceConfig.autoAdvance) {
          if (sequenceConfig.scheduleType === 'time') {
            advanceToNextValidScreen();
          } else {
            navigateToNextScreen();
          }
        } else {
          setCurrentPanfletoIndex(0);
        }
      } else {
        setCurrentPanfletoIndex(prev => prev + 1);
      }
    }
  }, [
    filteredPromotions.length,
    activeScreen?.layoutId,
    currentPanfletoIndex,
    sequenceConfig,
    navigateToNextScreen,
    advanceToNextValidScreen,
  ]);

  // Sincronizar currentScreenIndex quando a URL muda e há uma sequência ativa
  useEffect(() => {
    if (sequenceConfig.activeSequenceId && screenIdFromUrl) {
      const activeSequence = sequences.find(s => s.id === sequenceConfig.activeSequenceId);
      if (activeSequence) {
        const orderedScreens = [...activeSequence.screens].sort((a, b) => a.order - b.order);
        const screenIndex = orderedScreens.findIndex(item => item.screenId === screenIdFromUrl);
        if (screenIndex !== -1 && screenIndex !== currentScreenIndex) {
          goToSpecificScreen(screenIndex);
        }
      }
    }
  }, [screenIdFromUrl, sequenceConfig.activeSequenceId, sequences, currentScreenIndex, goToSpecificScreen]);

  // Verificar se a tela atual é válida para o dia atual ao carregar
  useEffect(() => {
    if (sequenceConfig.activeSequenceId && sequenceConfig.scheduleType === 'time') {
      // Se a tela atual não deve ser exibida hoje, avançar para a próxima válida
      if (!shouldDisplayCurrentScreen()) {
        advanceToNextValidScreen();
      }
    }
  }, [
    sequenceConfig.activeSequenceId,
    sequenceConfig.scheduleType,
    shouldDisplayCurrentScreen,
    advanceToNextValidScreen
  ]);

  // Ref para totalPages para evitar closures antigos no intervalo
  const totalPagesRef = useRef(totalPages);
  useEffect(() => {
    totalPagesRef.current = totalPages;
  }, [totalPages]);

  // Gerenciar paginação automática de produtos
  useEffect(() => {
    // Limpar timers anteriores
    if (paginationTimerRef.current) {
      clearInterval(paginationTimerRef.current);
      paginationTimerRef.current = null;
    }
    if (productPaginationTimerRef.current) {
      clearTimeout(productPaginationTimerRef.current);
      productPaginationTimerRef.current = null;
    }

    // Resetar barra de progresso (imperativo, sem re-render)
    if (progressBarRef.current) {
      progressBarRef.current.style.width = '0%';
    }

    // Se não há páginas suficientes ou é vídeo, não fazer nada
    // IMPORTANTE: totalPagesRef.current garante o valor atual
    const total = totalPagesRef.current;
    if (total <= 1 || activeScreen?.layoutId === 'video') {
      return;
    }

    // REGRA: O tempo de paginação não deve ser interpretado se houver uma sequência ativa
    // A sequência tem seu próprio controle de tempo
    if (sequenceConfig.activeSequenceId) {
      return;
    }

    // IMPORTANTE: Se a paginação está pausada, não criar timers
    if (isPaginationPaused) {
      return;
    }

    const duration = Math.max(1, timePerPage); // Tempo por página
    const intervalTime = 100; // 100ms

    // Configurar intervalo para barra de progresso (imperativo, 0 re-renders)
    let percent = 0;
    paginationTimerRef.current = setInterval(() => {
      percent = Math.min(percent + (intervalTime / (duration * 1000)) * 100, 100);
      if (progressBarRef.current) {
        progressBarRef.current.style.width = `${percent}%`;
      }
    }, intervalTime);

    // Configurar timeout para avançar página (usando timer separado)
    productPaginationTimerRef.current = setTimeout(() => {
      // Transição leve baseada apenas em opacidade/transform
      setCurrentPage(curr => {
        const next = curr + 1;
        return next >= total ? 0 : next;
      });
    }, duration * 1000);

    // Limpar timers ao desmontar ou quando dependências mudarem (incluindo currentPage)
    return () => {
      if (paginationTimerRef.current) {
        clearInterval(paginationTimerRef.current);
      }
      if (productPaginationTimerRef.current) {
        clearTimeout(productPaginationTimerRef.current);
      }
    };
  }, [currentPage, totalPages, timePerPage, activeScreen?.layoutId, sequenceConfig.activeSequenceId, isPaginationPaused]);

  // Gerenciar tempo de exibição para layouts não-vídeo
  useEffect(() => {
    // Limpar qualquer timer existente
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }

    // Não configurar timer se não houver sequência ativa ou autoAdvance estiver desativado
    if (!sequenceConfig.activeSequenceId || !sequenceConfig.autoAdvance) {
      return;
    }

    // Não configurar timer se estiver reproduzindo vídeo
    if (activeScreen?.layoutId === 'video' && isPlayingVideo && !videoEnded) {
      return;
    }

    // Configurar timer para layouts não-vídeo ou se o vídeo terminou
    if (activeScreen && (activeScreen.layoutId !== 'video' || videoEnded)) {
      // Verificar se há uma sequência ativa
      const activeSequence = sequences.find(s => s.id === sequenceConfig.activeSequenceId);

      if (activeSequence) {
        // Encontrar o item de sequência atual
        const sequenceItem = activeSequence.screens
          .sort((a, b) => a.order - b.order)
          .find((item, index) => index === currentScreenIndex);

        if (sequenceItem && activeScreen) {
          // Panfleto: um timer por produto; demais layouts: duração da sequência / tela / refresh
          const duration =
            activeScreen.layoutId === 'panfleto'
              ? Math.max(1, timePerPanfletoProduct)
              : sequenceItem.duration ||
                activeScreen.duration ||
                displayConfig.refreshInterval;

          // Configurar timer para avançar para a próxima tela
          autoAdvanceTimerRef.current = setTimeout(() => {
            // Para layout panfleto, só avançar se já mostrou todos os itens
            if (activeScreen && activeScreen.layoutId === 'panfleto') {
              if (currentPanfletoIndex >= filteredPromotions.length - 1) {
                // Usar a função que verifica dias válidos para avançar
                if (sequenceConfig.scheduleType === 'time') {
                  advanceToNextValidScreen();
                } else {
                  navigateToNextScreen();
                }
              } else {
                nextPanfletoItem();
              }
            } else {
              // Usar a função que verifica dias válidos para avançar
              if (sequenceConfig.scheduleType === 'time') {
                advanceToNextValidScreen();
              } else {
                navigateToNextScreen();
              }
            }
          }, duration * 1000);
        }
      }
    }

    return () => {
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }
    };
  }, [
    activeScreen,
    displayConfig.refreshInterval,
    sequenceConfig,
    sequences,
    currentScreenIndex,
    navigateToNextScreen,
    isPlayingVideo,
    videoEnded,
    currentPanfletoIndex,
    filteredPromotions.length,
    nextPanfletoItem,
    advanceToNextValidScreen,
    timePerPanfletoProduct
  ]);

  // Panfleto sem sequência: alternar produto a cada tempoPanfletoProduto (s)
  useEffect(() => {
    if (panfletoProductTimerRef.current) {
      clearTimeout(panfletoProductTimerRef.current);
      panfletoProductTimerRef.current = null;
    }
    if (sequenceConfig.activeSequenceId && sequenceConfig.autoAdvance) {
      return;
    }
    if (!activeScreen || activeScreen.layoutId !== 'panfleto') {
      return;
    }
    if (filteredPromotions.length <= 1) {
      return;
    }
    if (isPaginationPaused) {
      return;
    }
    if (panfletoPositionEditMode) {
      return;
    }
    const sec = Math.max(1, timePerPanfletoProduct);
    panfletoProductTimerRef.current = setTimeout(() => {
      setCurrentPanfletoIndex(prev =>
        prev >= filteredPromotions.length - 1 ? 0 : prev + 1
      );
    }, sec * 1000);
    return () => {
      if (panfletoProductTimerRef.current) {
        clearTimeout(panfletoProductTimerRef.current);
        panfletoProductTimerRef.current = null;
      }
    };
  }, [
    sequenceConfig.activeSequenceId,
    sequenceConfig.autoAdvance,
    activeScreen?.layoutId,
    filteredPromotions.length,
    timePerPanfletoProduct,
    currentPanfletoIndex,
    isPaginationPaused,
    panfletoPositionEditMode,
  ]);

  const panfletoProductIdsKey = useMemo(
    () => filteredPromotions.map(p => p.id).join(','),
    [filteredPromotions]
  );

  useEffect(() => {
    setCurrentPanfletoIndex(0);
  }, [screenIdFromUrl, panfletoProductIdsKey]);

  // Gerenciar finalização de vídeo
  const handleVideoEnded = useCallback(() => {
    setIsPlayingVideo(false);
    setVideoEnded(true);

    // Se tivermos uma sequência ativa com autoAdvance, ir para a próxima tela
    if (sequenceConfig.activeSequenceId && sequenceConfig.autoAdvance) {
      // Usar a função que verifica dias válidos para avançar
      if (sequenceConfig.scheduleType === 'time') {
        advanceToNextValidScreen();
      } else {
        navigateToNextScreen();
      }
    }
  }, [sequenceConfig, navigateToNextScreen, advanceToNextValidScreen]);

  // Gerenciar início de vídeo
  const handleVideoPlay = useCallback(() => {
    setIsPlayingVideo(true);
    setVideoEnded(false);
  }, []);

  // Manipular teclas de navegação
  useEffect(() => {
    const toggleFullscreen = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    };

    const handleKeyPress = (e: KeyboardEvent) => {
      // Ignorar atalhos se estiver digitando em um input
      const target = e.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
        return;
      }



      if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      } else if ((e.key === 'c' || e.key === 'C') && !isTotemMode) {
        setShowHeader(!showHeader);
      } else if ((e.key === '+' || e.key === '=') && activeScreen?.layoutId !== 'panfleto') {
        handleZoom(true);
      } else if ((e.key === '-' || e.key === '_') && activeScreen?.layoutId !== 'panfleto') {
        handleZoom(false);
      } else if (e.key === 'm' || e.key === 'M') {
        setShowSettings(!showSettings);
      } else if (e.key === 'n' || e.key === 'N') {
        setShowSequenceControls(!showSequenceControls);
      } else if ((e.key === 'l' || e.key === 'L') && activeScreen?.layoutId !== 'panfleto') {
        // Alternar modo de ajuste de posição
        setIsAdjustingPosition(!isAdjustingPosition);
      } else if (e.key === 'ArrowUp' && isAdjustingPosition) {
        // Mover conteúdo para cima quando no modo de ajuste
        e.preventDefault(); // Prevenir scroll da página
        adjustVerticalPosition(false);
      } else if (e.key === 'ArrowDown' && isAdjustingPosition) {
        // Mover conteúdo para baixo quando no modo de ajuste
        e.preventDefault(); // Prevenir scroll da página
        adjustVerticalPosition(true);
      } else if (e.key === 'ArrowRight' && isAdjustingPosition) {
        // Mover conteúdo para direita quando no modo de ajuste
        e.preventDefault(); // Prevenir scroll da página
        adjustHorizontalPosition(true);
      } else if (e.key === 'ArrowLeft' && isAdjustingPosition) {
        // Mover conteúdo para esquerda quando no modo de ajuste
        e.preventDefault(); // Prevenir scroll da página
        adjustHorizontalPosition(false);
      } else if (e.key === 'Enter' || e.key === 'Return') {
        // Enter = atalho do botão "Aplicar ajustes" (mesmo handler; não criar save paralelo)
        e.preventDefault();
        handleApplyAdjustments();
      } else if (e.key === 'p' || e.key === 'P') {
        // Alternar visibilidade da paginação
        e.preventDefault();
        setShowPagination(prev => !prev);
      } else if ((e.key === 'o' || e.key === 'O') && !isTotemMode) {
        // Alternar visibilidade do botão de configurações
        e.preventDefault();
        setShowSettingsButton(prev => !prev);
      } else if (
        (e.key === 't' || e.key === 'T') &&
        activeScreen?.layoutId === 'panfleto' &&
        !isTotemMode
      ) {
        e.preventDefault();
        setPanfletoPositionEditMode(prev => {
          return !prev;
        });
      } else if (
        (e.key === ' ' || e.key === 'Spacebar') &&
        !sequenceConfig.activeSequenceId &&
        ((activeScreen?.layoutId === 'simple2col' && totalPages > 1) ||
          (activeScreen?.layoutId === 'panfleto' && filteredPromotions.length > 1))
      ) {
        // Pausar/despausar paginação (colunas) ou rotação do panfleto
        e.preventDefault();
        setIsPaginationPaused(prev => !prev);
      } else if (e.key === 'ArrowRight' && sequenceConfig.activeSequenceId && !isAdjustingPosition) {
        // Usar a função que verifica dias válidos para avançar
        if (sequenceConfig.scheduleType === 'time') {
          advanceToNextValidScreen();
        } else {
          navigateToNextScreen();
        }
      } else if (e.key === 'ArrowLeft' && sequenceConfig.activeSequenceId && !isAdjustingPosition) {
        navigateToPreviousScreen();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [
    showHeader,
    setShowHeader,
    displayConfig.zoomLevel,
    showSettings,
    showSequenceControls,
    isAdjustingPosition,
    adjustVerticalPosition,
    adjustHorizontalPosition,
    handleApplyAdjustments,
    panfletoPositionEditMode,
    activeScreen,
    totalPages,
    filteredPromotions.length,
    setShowPagination,
    sequenceConfig.activeSequenceId,
    sequenceConfig.scheduleType,
    navigateToNextScreen,
    advanceToNextValidScreen,
    navigateToPreviousScreen,
    handleZoom,
    isTotemMode
  ]);

  // Buscar sequências das telas otimizadas (DEVE estar antes de qualquer early return)
  const sequencesList = useMemo(() => {
    if (!telasOtimizadas || telasOtimizadas.length === 0) {
      return [];
    }

    const nomesSequencias = extrairSequenciasUnicasDasTelas(telasOtimizadas);
    const sequences: Array<{ name: string; description: string; screenIds: number[] }> = [];

    for (const nomeSequencia of nomesSequencias) {
      const telasSequencia = buscarTelasPorSequencia(telasOtimizadas, nomeSequencia);
      if (telasSequencia.length > 0) {
        // Buscar descrição da sequência específica nas telas
        let descricaoSequencia = '';
        for (const tela of telasSequencia) {
          if (tela.configuracoes) {
            // IMPORTANTE: Usar função que busca descrição por NOME da sequência
            // Cada sequência pode ter sua própria descrição única
            const descricao = extrairDescricaoSequenciaPorNomeDoJson(tela.configuracoes, nomeSequencia);
            if (descricao) {
              descricaoSequencia = descricao;
              break; // Usar a primeira descrição encontrada para esta sequência específica
            }
          }
        }

        sequences.push({
          name: nomeSequencia,
          description: descricaoSequencia,
          screenIds: telasSequencia.map(t => t.id)
        });
      }
    }

    return sequences;
  }, [telasOtimizadas]);

  // Se não houver ID na URL, mostrar lista de telas da API
  if (!screenIdFromUrl) {
    if (loadingTelasList || loadingTelevisoes) {
      return (
        <div className="flex items-center justify-center h-screen bg-gray-50">
          <div className="text-center">
            <div className="flex flex-col items-center justify-center text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-lg font-medium">Carregando telas...</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 mb-8">Selecione uma Tela ou uma Televisão para Visualizar</h1>

          {/* Seção de Televisões */}
          {televisoesList.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-700 mb-6">Televisões</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {televisoesList.map((televisao) => {
                  const telaIds = televisaoService.stringToTelaIds(televisao.dsTelasCadastradas);
                  const quantidadeTelas = telaIds.length;

                  return (
                    <div
                      key={televisao.cdTelevisao}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow h-full flex flex-col"
                    >
                      {/* Header - título e ID */}
                      <div className="mb-4">
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">{televisao.nmTelevisao || 'Televisão sem nome'}</h3>
                        <p className="text-sm text-gray-500">ID: {televisao.cdTelevisao}</p>
                      </div>
                      {/* Content - área flexível que preenche o espaço */}
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center text-sm text-gray-600">
                          <span className="font-medium mr-2">Telas Vinculadas:</span>
                          <span className="font-semibold text-blue-600">
                            {quantidadeTelas} {quantidadeTelas === 1 ? 'tela' : 'telas'}
                          </span>
                        </div>
                      </div>
                      {/* Actions - sempre no final do card */}
                      <div className="mt-4">
                        <button
                          onClick={() => navigate(`/display/televisao/${televisao.cdTelevisao}`)}
                          className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors font-medium flex items-center justify-center gap-2"
                          disabled={quantidadeTelas === 0}
                        >
                          <Monitor size={18} />
                          {quantidadeTelas > 0 ? 'Visualizar Televisão' : 'Sem telas vinculadas'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Seção de Telas Individuais */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-700 mb-6">Telas Individuais</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {telasList.map((tela) => {
                // Converter tipo numérico para nome do layout
                const layoutId = TIPO_TO_LAYOUT[tela.tipo ?? 0] || 'simple2col';
                const layoutName = layouts.find(l => l.id === layoutId)?.name || 'Desconhecido';

                return (
                  <div
                    key={tela.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow h-full flex flex-col"
                  >
                    {/* Header - título e ID */}
                    <div className="mb-4">
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">{tela.nome || 'Tela sem nome'}</h3>
                      <p className="text-sm text-gray-500">Tela: {tela.id}</p>
                      {/* Exibir descrição se disponível */}
                      {tela.descricao && (
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">Descrição:</span> {tela.descricao}
                        </p>
                      )}
                    </div>
                    {/* Content - área flexível que preenche o espaço */}
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium mr-2">Layout:</span>
                        {layoutName}
                      </div>
                    </div>
                    {/* Actions - sempre no final do card */}
                    <div className="mt-4">
                      <button
                        onClick={() => navigate(`/display/${tela.id}`)}
                        className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors font-medium"
                      >
                        Visualizar Tela
                      </button>
                    </div>
                  </div>
                );
              })}
              {telasList.length === 0 && (
                <div className="col-span-full text-center py-16">
                  <p className="text-xl text-gray-500">Nenhuma tela disponível</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Se estiver carregando os dados da tela, mostrar loading
  if (loadingStyles) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-center">
          <div className="flex flex-col items-center justify-center text-white">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
            <p className="text-xl font-medium">Carregando tela...</p>
          </div>
        </div>
      </div>
    );
  }

  // Se não estiver carregando e não houver tela, mostrar erro
  if (!activeScreen) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Tela não encontrada</h2>
        <p className="text-gray-600">
          {screenIdFromUrl
            ? `A tela com ID ${screenIdFromUrl} não foi encontrada.`
            : 'Nenhuma tela especificada na URL.'}
        </p>
      </div>
    );
  }

  // Se estamos no modo de programação por dia e a tela atual não deve ser exibida hoje
  if (sequenceConfig.scheduleType === 'time' && !shouldDisplayCurrentScreen()) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Tela não programada para hoje</h2>
        <p className="text-gray-600 mb-4">
          Esta tela não está programada para ser exibida no dia de hoje.
        </p>
        <div className="flex justify-center">
          <button
            onClick={advanceToNextValidScreen}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
          >
            <SkipForward size={20} />
            Ir para próxima tela
          </button>
        </div>
      </div>
    );
  }

  const containerStyles = {
    simple2col: getGridClasses(),
    panfleto: "flex justify-center items-center h-full w-full",
    video: "flex justify-center items-center h-full w-full"
  };

  // For panfleto layout, only show the current item in the slideshow
  const displayedPromotions = activeScreen.layoutId === 'panfleto' && filteredPromotions.length > 0
    ? [filteredPromotions[currentPanfletoIndex]]
    : filteredPromotions;

  /** Transição entre produtos do panfleto (alinhada às colunas: slide; fade = só opacidade). */
  const getPanfletoProductTransitionClasses = () => {
    if (activeScreen?.layoutId !== 'panfleto') return '';
    return `${styles.panfletoProductShell} ${styles.panfletoProductsVt} ${styles.panfletoProductOuter}`;
  };

  // Verificar se o layout atual é do tipo que deve ser centralizado
  const isCenteredLayout = ['video', 'panfleto'].includes(activeScreen.layoutId);

  const handleTotemLogout = () => {
    authService.logout();
    totemModeService.disableTotemMode();
    navigate('/televisao', { replace: true });
  };

  // Function to render the appropriate content based on layout
  const renderContent = () => {
    if (!activeScreen) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <p className="text-xl text-gray-500 mb-4">Nenhuma tela selecionada</p>
          {!isTotemMode && (
            <button
              onClick={() => setShowSettings(true)}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
            >
              Configurações
            </button>
          )}
        </div>
      );
    }

    // Handle video layout
    if (activeScreen.layoutId === 'video') {
      // IMPORTANTE: Buscar vídeo do endpoint específico (não mais do JSON)
      const videoLoop = true; // Sempre em loop para telas de vídeo

      if (!activeScreen.id) {
        return (
          <div className="flex flex-col items-center justify-center h-full">
            <p className="text-xl text-gray-500">ID da tela não encontrado</p>
          </div>
        );
      }

      // Usar componente que carrega vídeo com autenticação (cria Blob URL)
      return (
        <VideoPlayerWithAuth
          screenId={activeScreen.id}
          videoLoop={videoLoop}
          onPlay={() => setIsPlayingVideo(true)}
          onPause={() => setIsPlayingVideo(false)}
          onEnded={() => {
            setVideoEnded(true);
            // Se não estiver em loop e tiver auto-advance, avançar para próxima tela
            if (!videoLoop && sequenceConfig.autoAdvance && sequenceConfig.activeSequenceId) {
              if (sequenceConfig.scheduleType === 'time') {
                advanceToNextValidScreen();
              } else {
                navigateToNextScreen();
              }
            }
          }}
        />
      );
    }

    // Handle panfleto layout
    if (activeScreen.layoutId === 'panfleto') {
      const hasPanfletoProducts = filteredPromotions.length > 0;
      // Only show the current panfleto item (com índice seguro quando a lista muda em tempo real)
      const safePanfletoIndex = hasPanfletoProducts
        ? Math.min(Math.max(currentPanfletoIndex, 0), filteredPromotions.length - 1)
        : 0;
      const currentPromo: Promotion =
        hasPanfletoProducts
          ? filteredPromotions[safePanfletoIndex]
          : {
              id: 0,
              name: '',
              price: 0,
              unit: '',
              validUntil: '',
              category: '',
              createdAt: new Date().toISOString(),
            };

      // Obter border-radius do layoutStyle para aplicar também no wrapper
      const borderRadius = activeScreen?.layoutStyle?.productContainer?.borderRadius;
      return (
        <div
          className={styles.centeredLayout}
          style={{
            top: `${getCurrentTopOffset()}px`,
            left: `${getCurrentLeftOffset()}px`
          }}
        >
          <div
            className={`${styles.productCard} ${getPanfletoProductTransitionClasses()}`}
            onClick={panfletoPositionEditMode ? undefined : nextPanfletoItem}
            style={{
              borderRadius: borderRadius ? `${borderRadius}px` : undefined,
              overflow: 'visible',
              cursor: panfletoPositionEditMode ? 'default' : undefined,
            }}
          >
            <PromoCard
              promo={currentPromo}
              layout={activeScreen.layoutId}
              layoutStyle={panfletoLayoutStyleComDraft}
              panfletoProdutoFotoViaApi={hasPanfletoProducts}
              panfletoHasProductsOnScreen={hasPanfletoProducts}
              panfletoTelaId={activeScreen.id}
              panfletoTelaFotoReloadToken={panfletoTelaFotoReloadToken}
              panfletoPositionEditMode={panfletoPositionEditMode}
              panfletoPlacementDraft={panfletoPlacementDraft}
              onPanfletoPlacementDraftChange={setPanfletoPlacementDraft}
              onPanfletoFieldStyleDraftChange={handlePanfletoFieldStyleDraftChange}
            />
          </div>
        </div>
      );
    }

    // Default to simple2col layout
    return (
      <div
        className={`${styles.listLayout} ${isAdjustingPosition ? styles.positionAdjustMode : ''}`}
        style={{
          top: `${getCurrentTopOffset()}px`,
          // Quando for 1 coluna, usar left negativo para permitir que o conteúdo chegue às extremidades
          // O leftOffset será aplicado de forma diferente para compensar o padding do mainContent
          left: numeroColunas === 1 
            ? `${getCurrentLeftOffset() - (activeScreen?.layoutId === 'simple2col' ? 24 : 0)}px` // Compensar padding do mainContent
            : `${getCurrentLeftOffset()}px`,
          // IMPORTANTE: Aplicar estilos especiais apenas quando for 1 coluna
          // Para 2 e 3 colunas, usar estilos padrão para que o grid funcione corretamente
          ...(numeroColunas === 1 ? { 
            paddingLeft: 0, 
            paddingRight: 0,
            paddingTop: 8,
            paddingBottom: 20,
            marginLeft: 0,
            marginRight: 0,
            width: '100vw', // Usar viewport width para ocupar toda a largura
            maxWidth: '100vw',
            position: 'relative' // Garantir que o posicionamento funcione corretamente
          } : {
            // Para 2 e 3 colunas: garantir que o container não tenha restrições de largura
            width: '100%',
            maxWidth: '100%',
            paddingLeft: 0,
            paddingRight: 0
          })
        }}
      >
        <div
          key={`products-${currentPage}`}
          className={`${containerStyles.simple2col} max-w-full ${styles.productsSlideFade} ${styles.productsContainer}`}
          style={{
            overflow: 'hidden',
            // Forçar composição na GPU e isolar custos de layout/pintura
            willChange: 'opacity, transform',
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            contain: 'layout paint style',
            // Gap entre produtos (verticalGap e horizontalGap do layoutStyle) - espaçamento interno entre produtos
            gap: activeScreen?.layoutStyle?.productContainer?.verticalGap
              ? `${activeScreen.layoutStyle.productContainer.verticalGap}px ${activeScreen.layoutStyle.productContainer.horizontalGap || 8}px`
              : undefined,
            // Aplicar largura da coluna apenas quando for 1 coluna
            ...(numeroColunas === 1 ? { 
              maxWidth: larguraColuna === 100 ? '100%' : `${larguraColuna}%`, 
              margin: larguraColuna === 100 ? '0' : '0 auto',
              width: '100%',
              // Garantir que não haja padding ou margin que impeça chegar às extremidades
              paddingLeft: 0,
              paddingRight: 0,
              marginLeft: larguraColuna === 100 ? 0 : 'auto',
              marginRight: larguraColuna === 100 ? 0 : 'auto'
            } : {})
          }}
        >
          {paginatedPromotions.length > 0 ? (
            paginatedPromotions.map((promo: Promotion, promoIndex: number) => {
              // Obter border-radius do layoutStyle para aplicar também no wrapper
              const borderRadius = activeScreen?.layoutStyle?.productContainer?.borderRadius;
              
              // Criar layoutStyle atualizado com os valores de externalSpacing
              // O PromoCard aplica o margin através do getContainerStyle(), então precisamos passar os valores atualizados
              // IMPORTANTE: Sempre usar os valores de externalSpacing diretamente (sem verificar > 0)
              // IMPORTANTE: Quando for 1 coluna, não aplicar marginLeft e marginRight para permitir chegar às extremidades
              const updatedLayoutStyle = activeScreen.layoutStyle ? {
                ...activeScreen.layoutStyle,
                productContainer: {
                  ...activeScreen.layoutStyle.productContainer,
                  // CRÍTICO: Usar exatamente o valor de externalSpacing, sem fallback para valores antigos
                  // Se o usuário digitar 0, deve usar 0. Se digitar 5, deve usar 5. Sempre o valor digitado.
                  marginTop: externalSpacing.marginTop,
                  marginRight: numeroColunas === 1 ? 0 : externalSpacing.marginRight,
                  marginBottom: externalSpacing.marginBottom,
                  marginLeft: numeroColunas === 1 ? 0 : externalSpacing.marginLeft,
                }
              } : activeScreen.layoutStyle;
              
              return (
                <div
                  key={`${activeScreen.id}-${currentPage}-${promo.id}-${promoIndex}`}
                  className={`${styles.productCard} h-full w-full`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    // Composição GPU por card
                    willChange: 'opacity, transform',
                    transform: 'translateZ(0)',
                    backfaceVisibility: 'hidden',
                    contain: 'layout paint style',
                    // Aplicar border-radius no wrapper também para que a margem seja cortada
                    borderRadius: borderRadius ? `${borderRadius}px` : undefined,
                  }}
                >
                  <MemoPromoCard promo={promo} layout={activeScreen.layoutId} layoutStyle={updatedLayoutStyle} />
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 col-span-2">
              <p className="text-xl text-gray-500">Nenhuma promoção encontrada</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Verificar se há uma sequência ativa
  const activeSequence = sequenceConfig.activeSequenceId
    ? sequences.find(s => s.id === sequenceConfig.activeSequenceId)
    : null;

  // Obter o dia da semana atual (0 = domingo, 1 = segunda, etc.)
  const currentDayOfWeek = new Date().getDay();
  const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

  return (
    <div 
      className={styles.displayContainer}
      style={{
        // Aplicar compensação de zoom para TV
        // Desabilitado para teste - não aplicar scale
        // ...(shouldApplyZoomCompensation ? {
        //   transform: `scale(${zoomCompensation})`,
        //   transformOrigin: 'top left',
        //   width: `${100 / zoomCompensation}%`,
        //   height: `${100 / zoomCompensation}%`
        // } : {}),
        // Permitir scroll horizontal no mobile se necessário
        ...(deviceInfo.isMobile ? {
          overflowX: 'auto',
          overflowY: 'auto'
        } : {
          overflow: 'hidden'
        })
      }}
    >
      {/* Success Notification — portal no body, acima do TopMenu (z-2000) */}
      {showSuccessNotification &&
        createPortal(
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              top: '1rem',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              backgroundColor: '#166534',
              color: '#ffffff',
              border: '1px solid #14532d',
              boxShadow: '0 10px 25px rgba(0,0,0,0.35)',
              fontWeight: 600,
              fontSize: '0.95rem',
              pointerEvents: 'none',
              opacity: 1,
            }}
          >
            ✓ Ajustes salvos com sucesso
          </div>,
          document.body
        )}

      {/* Position Adjustment Indicator — acima do TopMenu */}
      {isAdjustingPosition &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: showSuccessNotification ? '4.25rem' : '1rem',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              backgroundColor: '#2563eb',
              color: '#ffffff',
              boxShadow: '0 10px 25px rgba(0,0,0,0.35)',
              fontSize: '0.875rem',
              maxWidth: 'min(96vw, 42rem)',
            }}
          >
            <span>Modo de Ajuste de Posição Ativo</span>
            <span style={{ opacity: 0.9 }}>
              (Use ↑↓←→ para ajustar, Enter para aplicar, L para sair)
            </span>
            <span
              style={{
                backgroundColor: '#ffffff',
                color: '#2563eb',
                padding: '0.125rem 0.5rem',
                borderRadius: '0.25rem',
                marginLeft: '0.5rem',
                fontSize: '0.8rem',
                fontWeight: 600,
              }}
            >
              X: {getCurrentLeftOffset()}px | Y: {getCurrentTopOffset()}px
            </span>
          </div>,
          document.body
        )}

      <InchSimulationStage simulation={inchSimulation}>
      {/* Background Layer - Imagem de fundo SEM opacidade/blur (fica normal) */}
      {/* Para panfleto, não aplicar imagem de fundo no backgroundLayer - o panfleto tem seu próprio layout */}
      <div
        className={styles.backgroundLayer}
        style={{
          backgroundColor: activeScreen?.backgroundColor || '#000000',
          backgroundImage: (() => {
            // Para panfleto, não usar imagem de fundo no backgroundLayer
            if (activeScreen?.layoutId === 'panfleto') {
              return 'none';
            }

            // IMPORTANTE: Usar APENAS imagem de fundo do layoutStyle (vindo das configurações da API)
            // Não usar fallback para backgroundImage antigo
            const bgImage = activeScreen?.layoutStyle?.productContainer?.backgroundImage;
            const bgVisible = activeScreen?.layoutStyle?.productContainer?.backgroundImageVisible !== false;

            // Só usar a imagem se ela existir nas configurações da API e estiver visível
            if (bgImage && typeof bgImage === 'string' && bgImage.trim().length > 0 && bgVisible && activeScreen?.layoutId !== 'video') {
              return `url(${bgImage})`;
            }

            // Não usar fallback - se não estiver nas configurações da API, não mostrar
            return 'none';
          })(),
        }}
      />

      {/* Content Layer */}
      <div 
        className={styles.contentLayer}
        style={{
          // Desabilitar scroll horizontal quando for 1 coluna para evitar barra de scroll feia (exceto no mobile)
          ...(activeScreen?.layoutId === 'simple2col' && numeroColunas === 1 && !deviceInfo.isMobile ? {
            overflowX: 'hidden'
          } : {}),
          // No mobile, permitir scroll se necessário
          ...(deviceInfo.isMobile ? {
            overflowX: 'auto',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch' // Scroll suave no iOS
          } : {})
        }}
      >
        {/* Sequence Controls */}
        {!isTotemMode && activeSequence && (
          <div
            className={styles.sequenceControls}
            style={{
              opacity: showSequenceControls ? 1 : 0,
              pointerEvents: showSequenceControls ? 'auto' : 'none',
              backgroundColor: showSequenceControls ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)'
            }}
          >
            <button
              onClick={navigateToPreviousScreen}
              className="bg-white bg-opacity-25 text-white p-2 rounded-full hover:bg-opacity-50 transition-all"
              title="Tela Anterior"
            >
              <SkipBack size={24} />
            </button>

            <div className="px-4 py-2 bg-white bg-opacity-20 rounded-lg text-white text-sm">
              {currentScreenIndex + 1} / {activeSequence.screens.length}
              {sequenceConfig.scheduleType === 'time' && (
                <span className="ml-2 flex items-center">
                  <Calendar size={16} className="mr-1" />
                  {dayNames[currentDayOfWeek]}
                </span>
              )}
            </div>

            <button
              onClick={sequenceConfig.scheduleType === 'time' ? advanceToNextValidScreen : navigateToNextScreen}
              className="bg-white bg-opacity-25 text-white p-2 rounded-full hover:bg-opacity-50 transition-all"
              title="Próxima Tela"
            >
              <SkipForward size={24} />
            </button>
          </div>
        )}

        {/* Main Content */}
        <div 
          className={styles.mainContent} 
          style={{
          justifyContent: isCenteredLayout ? 'center' : 'flex-start',
          alignItems: isCenteredLayout ? 'center' : 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          // Panfleto: sem zoom; colunas/vídeo: displayConfig.zoomLevel
          transform: `scale(${activeScreen?.layoutId === 'panfleto' ? 1 : displayConfig.zoomLevel})`,
          transformOrigin: 'center top',
          position: 'relative',
          // No mobile, sempre permitir scroll
          ...(deviceInfo.isMobile ? {
            overflowX: 'auto',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            minWidth: '100%',
            width: '100%'
          } : {
            // IMPORTANTE: Quando o cabeçalho estiver ativado (showHeader = true), ocultar scrolls para visualização limpa
            overflowY: (isCenteredLayout || isTotemMode || showHeader) ? 'hidden' : 'auto',
            // Remover padding horizontal quando for 1 coluna para permitir chegar às extremidades
            // E permitir overflow horizontal para que o conteúdo possa ir além das bordas
            // IMPORTANTE: Quando o cabeçalho estiver ativado (showHeader = true), ocultar scroll horizontal
            overflowX: (isTotemMode ||
              showHeader ||
              (activeScreen?.layoutId === 'simple2col' && numeroColunas === 1) ||
              (activeScreen && ['panfleto', 'video'].includes(activeScreen.layoutId)))
              ? 'hidden'
              : 'auto',
          }),
          ...(activeScreen?.layoutId === 'simple2col' && numeroColunas === 1 ? { 
            paddingLeft: 0, 
            paddingRight: 0
          } : {}),
          ...(activeScreen &&
          ['panfleto', 'video'].includes(activeScreen.layoutId) &&
          !deviceInfo.isMobile
            ? {
                padding: 0,
                paddingTop: 0,
                paddingRight: 0,
                paddingBottom: 0,
                paddingLeft: 0,
              }
            : {})
        }}>
          <div
            key={`display-screen-${screenIdFromUrl ?? 0}-${activeScreen?.layoutId ?? 'none'}`}
            className={`${styles.screenSwapFade} ${styles.displayContentVt}${
              activeScreen?.layoutId === 'panfleto' ? ` ${styles.screenSwapFadePanfleto}` : ''
            }`}
          >
          {/* Controles de Paginação (centralizado na área de visualização, independente da posição dos produtos) */}
          {activeScreen?.layoutId === 'simple2col' && totalPages > 1 && showPagination && (
            <div
              className="fixed z-20 pointer-events-none"
              style={{
                top: '16px',
                left: '50%',
                transform: `translate(-50%, 0) scale(${1 / displayConfig.zoomLevel})`,
                transformOrigin: 'center top'
              }}
            >
              <div className="inline-flex items-center gap-4 px-4 py-2 rounded-lg bg-black bg-opacity-50 backdrop-blur-sm pointer-events-auto">
                <button
                  onClick={() => {
                    const newPage = Math.max(0, currentPage - 1);
                    changePageWithTransition(newPage);
                  }}
                  disabled={currentPage === 0 || isTransitioning}
                  className={`px-4 py-2 rounded-lg transition-colors ${currentPage === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                >
                  Anterior
                </button>
                <span className="text-white font-medium">
                  Página {currentPage + 1} de {totalPages} ({productsPerPage} produtos por página)
                </span>
                <button
                  onClick={() => {
                    const newPage = Math.min(totalPages - 1, currentPage + 1);
                    changePageWithTransition(newPage);
                  }}
                  disabled={currentPage >= totalPages - 1 || isTransitioning}
                  className={`px-4 py-2 rounded-lg transition-colors ${currentPage >= totalPages - 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                >
                  Próxima
                </button>
                <span className="text-white text-xs opacity-75 ml-2">
                  Total de {filteredPromotions.length} produtos (Aperte P para esconder a paginação)
                </span>
              </div>
            </div>
          )}
          {renderContent()}
          </div>
        </div>
        {/* Barra de Progresso - visível apenas no modo visualização normal e quando o cabeçalho estiver visível */}
        {!isTotemMode && showHeader &&
          ((activeScreen?.layoutId === 'simple2col' && totalPages > 1) ||
            (activeScreen?.layoutId === 'panfleto' && filteredPromotions.length > 1)) && (
          <div className="fixed bottom-0 left-0 right-0 h-2 bg-gray-800 z-50">
            <div
              ref={el => (progressBarRef.current = el)}
              className="h-full bg-blue-500 transition-all duration-100 ease-linear"
              style={{ width: '0%' }}
            />
          </div>
        )}
      </div>
      </InchSimulationStage>
        <DisplayChromePortal>
        {/* Settings Button */}
        {showSettingsButton && (
          <>
            {!isTotemMode ? (
              // Estilo completo para visualização manual
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`${styles.settingsButton} flex items-center gap-3 px-4 py-2 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-lg shadow-lg transition-all duration-200 focus:outline-none`}
                title="Configurações de Exibição (M)"
              >
                <span className="text-black font-semibold text-xl">Configurações</span>
                <Settings size={26} className="text-black" />
              </button>
            ) : (
              // Estilo pequeno e transparente para modo totem
              <>
                <button
                  onClick={handleTotemLogout}
                  className="absolute top-4 left-4 bg-transparent hover:bg-transparent focus:outline-none"
                  style={{ zIndex: 1000, pointerEvents: 'auto' }}
                  title="Sair do modo totem"
                >
                  <LogOut size={24} className="text-white opacity-0 hover:opacity-100 transition-opacity duration-200" />
                </button>
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className={`${styles.settingsButton} bg-transparent hover:bg-transparent focus:outline-none`}
                  style={{ zIndex: 1000 }}
                  title="Configurações de Exibição (M)"
                >
                  <Settings size={24} className="text-white opacity-0 hover:opacity-100 transition-opacity duration-200" />
                </button>
              </>
            )}
          </>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <div className={styles.settingsPanel}>
            <div className={styles.settingsPanelHeader}>
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  Configurações de Exibição
                  <span className="text-sm text-gray-500 font-normal">(M)</span>
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded hover:bg-gray-100"
                  title="Fechar menu"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className={`${styles.settingsPanelContent} space-y-4`}>
              <InchSimulationControl simulation={inchSimulation} />

              {activeScreen?.layoutId !== 'panfleto' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zoom ({Math.round(displayConfig.zoomLevel * 100)}%)
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleZoom(false)}
                    className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                  >
                    <ZoomOut size={18} />
                    Diminuir (-)
                  </button>
                  <button
                    onClick={() => handleZoom(true)}
                    className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                  >
                    <ZoomIn size={18} />
                    Aumentar (+)
                  </button>
                </div>
              </div>
              )}

              {activeScreen?.layoutId === 'panfleto' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tempo por produto no panfleto (s)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={600}
                    value={timePerPanfletoProduct}
                    onChange={(e) => setTimePerPanfletoProduct(Math.max(1, Number(e.target.value) || 1))}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Padrão: 10 s.
                  </p>
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setPanfletoPositionEditMode(prev => {
                          return !prev;
                        });
                      }}
                      disabled={savingPanfletoPlacements}
                      className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                    >
                      {panfletoPositionEditMode
                        ? 'Encerrar modo personalizar (T)'
                        : 'Personalizar posições (T)'}
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      Personalize a posição de todos elementos visuais na tela de panfleto.
                    </p>
                    {!panfletoPositionEditMode && (panfletoPlacementDraft || panfletoFieldStyleDraft) ? (
                      <p className="text-xs font-medium text-amber-700 mt-2">
                        Há ajustes locais não salvos. Pressione Enter ou use &quot;Aplicar ajustes&quot; para gravar.
                      </p>
                    ) : null}
                    {panfletoPositionEditMode ? (
                      <p className="text-xs font-medium text-indigo-700 mt-2">
                        Rotação entre produtos pausada neste modo.
                      </p>
                    ) : null}
                  </div>
                </div>
              )}

              {/* Configuração de Colunas - apenas para simple2col */}
              {activeScreen?.layoutId === 'simple2col' && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowColumnConfig(!showColumnConfig)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-700">Configuração de Colunas</span>
                    {showColumnConfig ? (
                      <ChevronUp size={18} className="text-gray-500" />
                    ) : (
                      <ChevronDown size={18} className="text-gray-500" />
                    )}
                  </button>

                  {showColumnConfig && (
                    <div className="mt-3 space-y-4 ml-0 pl-0 border-l-0">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Número de Colunas
                        </label>
                        <select
                          value={numeroColunas}
                          onChange={(e) => setNumeroColunas(Number(e.target.value))}
                          className="w-full p-2 border border-gray-300 rounded-lg"
                        >
                          <option value={1}>1 Coluna</option>
                          <option value={2}>2 Colunas</option>
                          <option value={3}>3 Colunas</option>
                        </select>
                      </div>

                      {/* Slider de Largura da Coluna - apenas quando for 1 coluna */}
                      {numeroColunas === 1 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Largura da Coluna ({larguraColuna}%)
                          </label>
                          <input
                            type="range"
                            min="20"
                            max="100"
                            value={larguraColuna}
                            onChange={(e) => setLarguraColuna(Number(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            style={{
                              // Calcular porcentagem relativa ao range (20-100)
                              // Range total: 100 - 20 = 80
                              // Porcentagem no slider: ((larguraColuna - 20) / 80) * 100
                              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((larguraColuna - 20) / 80) * 100}%, #e5e7eb ${((larguraColuna - 20) / 80) * 100}%, #e5e7eb 100%)`
                            }}
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>20%</span>
                            <span>100%</span>
                          </div>
                        </div>
                      )}

                      {/* Espaçamento Externo */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Espaçamento Externo (px)
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Superior</label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={externalSpacing.marginTop}
                              onChange={(e) => {
                                const value = Number(e.target.value) || 0;
                                setExternalSpacing(prev => ({ ...prev, marginTop: value }));
                              }}
                              className="w-full p-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Direita</label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={externalSpacing.marginRight}
                              onChange={(e) => {
                                const value = Number(e.target.value) || 0;
                                setExternalSpacing(prev => ({ ...prev, marginRight: value }));
                              }}
                              disabled={numeroColunas === 3}
                              className={`w-full p-2 border border-gray-300 rounded-lg ${numeroColunas === 3 ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Inferior</label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={externalSpacing.marginBottom}
                              onChange={(e) => {
                                const value = Number(e.target.value) || 0;
                                setExternalSpacing(prev => ({ ...prev, marginBottom: value }));
                              }}
                              className="w-full p-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Esquerda</label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={externalSpacing.marginLeft}
                              onChange={(e) => {
                                const value = Number(e.target.value) || 0;
                                setExternalSpacing(prev => ({ ...prev, marginLeft: value }));
                              }}
                              disabled={numeroColunas === 3}
                              className={`w-full p-2 border border-gray-300 rounded-lg ${numeroColunas === 3 ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Paginação de colunas — não usado no panfleto */}
              {activeScreen?.layoutId !== 'panfleto' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Produtos por Página
                  </label>
                  <input
                    type="number"
                    value={productsPerPage === 0 ? '' : productsPerPage}
                    onChange={(e) => {
                      // Permitir digitar livremente (sem validação imediata para permitir valores como 2, 3, 10, 11, etc)
                      const rawValue = e.target.value;
                      // Se vazio, setar 0 (a lógica de paginação trata como 1)
                      if (rawValue === '') {
                        setProductsPerPage(0);
                        setCurrentPage(0);
                        return;
                      }
                      const numValue = Number(rawValue);
                      if (!isNaN(numValue)) {
                        setProductsPerPage(numValue);
                        setCurrentPage(0); // Resetar para primeira página ao mudar
                      }
                    }}
                    onBlur={(e) => {
                      // Validar no blur: mínimo 1, máximo 100
                      // Se vazio ou 0, colocar 1
                      const value = Math.max(1, Math.min(100, Number(e.target.value) || 1));
                      setProductsPerPage(value);
                    }}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    min="1"
                    max="100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tempo por Página (s)
                  </label>
                  <input
                    type="number"
                    value={timePerPage}
                    onChange={(e) => setTimePerPage(Math.max(1, Number(e.target.value)))}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    min="1"
                  />
                </div>
              </div>
              )}
              {activeScreen?.layoutId !== 'panfleto' && (
              <div className="mb-4">
                {activeScreen?.layoutId === 'simple2col' ? (
                  <p className="text-xs text-gray-500 mt-1">
                    Atual: {paginatedPromotions.length} produtos nesta página ({totalProductsCount} total)
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1 italic">
                    Aplicado apenas em telas com layout de 2 colunas
                  </p>
                )}
              </div>
              )}

              {activeSequence && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sequência Ativa
                  </label>
                  <div className="flex justify-between items-center px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
                    <span className="font-medium text-blue-800">{activeSequence.name}</span>
                    <div className="flex items-center gap-1 text-sm text-blue-600">
                      <PlaySquare size={16} />
                      <span>{currentScreenIndex + 1} / {activeSequence.screens.length}</span>
                      {sequenceConfig.scheduleType === 'time' && (
                        <span className="ml-1 flex items-center">
                          <Calendar size={14} className="mr-1" />
                          {dayNames[currentDayOfWeek]}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() => setShowPagination(!showPagination)}
                className={`w-full px-4 py-2 rounded-lg flex items-center justify-center gap-2 
                  ${showPagination ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors`}
              >
                {showPagination ? 'Ocultar' : 'Exibir'} controles de paginação (P)
              </button>

              {/* Botão de pausar paginação - só aparece quando não há sequência ativa */}
              {!sequenceConfig.activeSequenceId &&
                ((activeScreen?.layoutId === 'simple2col' && totalPages > 1) ||
                  (activeScreen?.layoutId === 'panfleto' && filteredPromotions.length > 1)) && (
                <button
                  onClick={() => setIsPaginationPaused(!isPaginationPaused)}
                  className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  {isPaginationPaused ? (
                    <>
                      <Play size={18} />
                      {activeScreen?.layoutId === 'panfleto' ? 'Retomar rotação (Espaço)' : 'Retomar Paginação (Espaço)'}
                    </>
                  ) : (
                    <>
                      <Pause size={18} />
                      {activeScreen?.layoutId === 'panfleto' ? 'Pausar rotação do panfleto (Espaço)' : 'Pausar Paginação (Espaço)'}
                    </>
                  )}
                </button>
              )}

              {!isTotemMode && (
                <button
                  onClick={() => setShowHeader(!showHeader)}
                  className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  {showHeader ? 'Ocultar' : 'Mostrar'} Cabeçalho (C)
                </button>
              )}

              <button
                onClick={() => document.documentElement.requestFullscreen()}
                className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                Modo Tela Cheia (F)
              </button>

              {activeSequence && (
                <button
                  onClick={() => setShowSequenceControls(!showSequenceControls)}
                  className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  {showSequenceControls ? 'Ocultar' : 'Mostrar'} Controles de Sequência (N)
                </button>
              )}

              {activeScreen?.layoutId !== 'panfleto' && (
              <button
                onClick={() => setIsAdjustingPosition(!isAdjustingPosition)}
                className={`w-full px-4 py-2 rounded-lg flex items-center justify-center gap-2 
                  ${isAdjustingPosition ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors`}
              >
                {isAdjustingPosition ? 'Desativar' : 'Ativar'} Ajuste de Posição (L)
                {(getCurrentTopOffset() !== 0 || getCurrentLeftOffset() !== 0) && (
                  <span className="ml-1 text-xs bg-white text-blue-600 px-2 py-1 rounded">
                    X:{getCurrentLeftOffset()} Y:{getCurrentTopOffset()}
                  </span>
                )}
              </button>
              )}

              {!isTotemMode && (
                <button
                  onClick={() => setShowSettingsButton(!showSettingsButton)}
                  className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  {showSettingsButton ? 'Ocultar' : 'Mostrar'} Botão de Configurações (O)
                </button>
              )}

              <button
                onClick={handleApplyAdjustments}
                disabled={savingPosition || savingPanfletoPlacements || !activeScreen}
                className={`w-full px-4 py-2 rounded-lg flex items-center justify-center gap-2 
                  ${savingPosition || savingPanfletoPlacements || !activeScreen ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'} text-white transition-colors`}
              >
                {savingPosition || savingPanfletoPlacements ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <RefreshCw size={18} />
                    Aplicar ajustes (Enter)
                  </>
                )}
              </button>
            </div>
          </div>
        )}
        </DisplayChromePortal>

      <InchSimulationBadge simulation={inchSimulation} />

      {/* Fullscreen Overlay for Totem Mode */}
      {isTotemMode && !isFullscreen && !loadingStyles && !loadingTelasList && (
        <button
          type="button"
          className="fixed inset-0 z-[9999] w-full h-full bg-black bg-opacity-95 flex flex-col items-center justify-center cursor-pointer transition-opacity duration-500 focus:outline-none"
          onClick={() => {
            totemModeService.requestFullscreen().catch(err => console.error(err));
            // Unmute logic: Access the current video element if available and unmute it
            if (videoRef.current) {
              console.log('User interaction (DisplayPage): Unmuting video...');
              videoRef.current.muted = false;
            }
          }}
        >
          <div className="animate-bounce mb-8 p-6 bg-white bg-opacity-10 rounded-full">
            <Maximize size={64} className="text-white opacity-90" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center px-4 drop-shadow-lg">
            Clique para ativar a Tela Cheia
          </h2>
        </button>
      )}
    </div>
  );
};

export default DisplayPage; 